import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  limit,
  writeBatch,
  serverTimestamp,
  onSnapshot,
    increment,
} from 'firebase/firestore';
import { db, auth,storage } from '../../../../firebase/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { runTransaction } from 'firebase/firestore';
import NewsManagementService from './NewsManagementService';
import { ref, uploadBytes, getDownloadURL, getMetadata, updateMetadata, listAll } from 'firebase/storage';
import ConversationMemorySystem from '../ConversationMemorySystem';

async function uploadAudioBlob(audioBlob, userId, podcastMetadata = {}) {
  try {
    const timestamp = Date.now();
    const audioRef = ref(storage, `podcasts/${userId}/${timestamp}.mp3`);
    
    // ✅ Create rich metadata for the audio file
    const metadata = {
      contentType: 'audio/mpeg',
      customMetadata: {
        // Podcast information
        podcastTitle: podcastMetadata.title || 'Untitled Podcast',
        podcastType: podcastMetadata.type || 'custom',
        duration: String(podcastMetadata.duration || 0),
        
        // User information
        userId: userId,
        preptuneUserId: podcastMetadata.preptuneUserId || '',
        
        // Hosts and guests
        hosts: JSON.stringify(podcastMetadata.hosts || ['Host 1', 'Host 2']),
        guest: podcastMetadata.guest || '',
        
        // Modules used
        modulesUsed: JSON.stringify(podcastMetadata.modulesUsed || []),
        
        // YouTube information (if applicable)
        youtubeVideoId: podcastMetadata.youtubeVideoId || '',
        youtubeVideoTitle: podcastMetadata.youtubeVideoTitle || '',
        
        // Timestamps
        generatedAt: new Date().toISOString(),
        uploadedAt: new Date().toISOString(),
        
        // Daily brief specific
        isDailyBrief: String(podcastMetadata.type === 'daily-brief'),
        dailyBriefDate: podcastMetadata.generatedFor || '',
        
        // Tags for searchability
        tags: JSON.stringify(podcastMetadata.tags || []),
        
        // Original filename
        originalFilename: `${podcastMetadata.title || 'podcast'}_${timestamp}.mp3`,
        
        // Version for future migrations
        metadataVersion: '1.0'
      }
    };
    
    // Upload with metadata
    await uploadBytes(audioRef, audioBlob, metadata);
    const downloadUrl = await getDownloadURL(audioRef);
    
    console.log('✅ Audio uploaded with metadata:', {
      path: `podcasts/${userId}/${timestamp}.mp3`,
      title: metadata.customMetadata.podcastTitle,
      size: audioBlob.size
    });
    
    return downloadUrl;
  } catch (error) {
    console.error('❌ Failed to upload audio with metadata:', error);
    throw error;
  }
}

class FirestorePersistentStorageService {
constructor() {
  this.currentUser = null;
  this.preptuneUserId = null;
  this.isInitialized = false;
  this.unsubscribeAuth = null;
  this.memoryService = null; // ADD THIS LINE
  this.localStorageService = null;
  this.initializationPromise = null;
  this.authStateReady = false;
  this.newsService = null;

  this.db = db;
  this.userId = null;

  // Admin configuration
  this.adminEmails = [
    'kogolof1@montclair.edu',
    'franklyn@preptune.com',
    'support@preptune.com'
  ];

  // Trial configuration
  this.TRIAL_DURATION_DAYS = 60;
  this.TOTAL_TRIAL_MINUTES = 20;

  // Auto-initialize auth listener with delay to ensure Firebase is ready
  setTimeout(() => {
    this.initializeAuthListener();
  }, 100);
}


  // Initialize authentication listener
  initializeAuthListener() {
    if (this.unsubscribeAuth) {
      console.log('Auth listener already initialized');
      return this.unsubscribeAuth;
    }

    console.log('Setting up auth listener...');
    
    this.unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user?.email || 'No user');
      this.authStateReady = true;
      
      if (user && user.uid !== this.currentUser?.uid) {
        // New user or different user
        this.currentUser = user;
        console.log('Setting currentUser to:', user.email);
        
        try {
          await this.initializeUserData();
          console.log('User data initialized for:', user.email, 'Preptune ID:', this.preptuneUserId);
        } catch (error) {
          console.error('Failed to initialize user data:', error);
        }
      } else if (!user) {
        // User signed out
        console.log('User signed out - clearing all state');
        this.currentUser = null;
        this.preptuneUserId = null;
        this.isInitialized = false;
        this.clearAllLocalStorage();
      } else if (user && user.uid === this.currentUser?.uid) {
        // Same user, ensure currentUser is set
        console.log('Same user confirmed:', user.email);
        this.currentUser = user;
      }
    });

    return this.unsubscribeAuth;
  }

  // Wait for auth state to be ready
  async waitForAuthState(timeoutMs = 15000) {
    const startTime = Date.now();
    
    while (!this.authStateReady && (Date.now() - startTime) < timeoutMs) {
      console.log('Waiting for auth state...');
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    if (!this.authStateReady) {
      throw new Error('Auth state not ready within timeout');
    }
    
    return true;
  }

  // Enhanced initialization method
  async initialize() {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this._performInitialization();
    return this.initializationPromise;
  }

  async _performInitialization() {
    try {
      console.log('Starting service initialization...');
      
      // Ensure auth listener is set up
      if (!this.unsubscribeAuth) {
        this.initializeAuthListener();
      }
      
      // Wait for auth state to be determined
      await this.waitForAuthState();
      
      if (this.currentUser) {
        console.log('User authenticated during init:', this.currentUser.email);
        if (!this.isInitialized) {
          await this.initializeUserData();
        }
        console.log('Service fully initialized');
        return true;
      } else {
        console.log('No user authenticated during init');
        return false;
      }
    } catch (error) {
      console.error('Error during initialization:', error);
      throw error;
    }
  }

  // Clear all localStorage data
  clearAllLocalStorage() {
    try {
      const keysToRemove = [
        'hume-evi-persistent-data',
        'hume-evi-conversations', 
        'voice-journal-entries',
        'preptune-voice-preference'
      ];

      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        console.log('Removed localStorage key:', key);
      });

      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('hume-conversation-') || key.startsWith('preptune-')) {
          localStorage.removeItem(key);
          console.log('Removed localStorage key:', key);
        }
      });

      console.log('All localStorage cleared for sign out');
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
    }
  }

  // FIXED: Calculate trial status with proper timezone handling
calculateTrialStatus(userData) {
  // Admin users don't have trials
  if (this.isAdmin()) {
    return {
      daysLeft: Infinity,
      minutesLeft: Infinity,
      totalMinutesUsed: 0,
      totalMinutesAllowed: this.TOTAL_TRIAL_MINUTES,
      trialEndDate: null,
      isExpired: false,
      percentageUsed: 0,
      isAdmin: true
    };
  }

  const now = new Date();
  const createdAt = userData.createdAt?.toDate ? userData.createdAt.toDate() : new Date(userData.createdAt);
  
  // FIXED: Calculate end date properly accounting for timezone
  const trialEndDate = new Date(createdAt);
  trialEndDate.setDate(trialEndDate.getDate() + this.TRIAL_DURATION_DAYS);
  
  // FIXED: Use Math.floor instead of Math.ceil to prevent showing 61 days
  const timeDiff = trialEndDate.getTime() - now.getTime();
  const daysLeft = Math.max(0, Math.floor(timeDiff / (1000 * 60 * 60 * 24)));
  
  // FIXED: Use actual remaining time from timer instead of calculating from usage
  const totalMinutesUsed = userData.trialUsage?.totalMinutesUsed || 0;
  const callTimerRemaining = userData.callTimer?.remainingTime || (this.TOTAL_TRIAL_MINUTES * 60);
  const minutesLeft = Math.max(0, Math.floor(callTimerRemaining / 60));
  
  const isExpired = daysLeft <= 0 || minutesLeft <= 0;
  
  console.log('Trial Status Calculation:', {
    createdAt: createdAt.toISOString(),
    trialEndDate: trialEndDate.toISOString(),
    now: now.toISOString(),
    timeDiff,
    daysLeft,
    totalMinutesUsed,
    callTimerRemaining,
    minutesLeft,
    isExpired
  });
  
  return {
    daysLeft,
    minutesLeft,
    totalMinutesUsed,
    totalMinutesAllowed: this.TOTAL_TRIAL_MINUTES,
    trialEndDate,
    isExpired,
    percentageUsed: Math.round((totalMinutesUsed / this.TOTAL_TRIAL_MINUTES) * 100),
    isAdmin: false
  };
}
/**
 * Save FCM token to user's Firestore document
 * Tokens are used by backend Cloud Functions to send push notifications
 */
async saveFCMToken(deviceInfo) {
  if (!this.userId) {
    console.error('❌ No user ID available');
    throw new Error('User not authenticated');
  }

  try {
    const userRef = doc(this.db, 'users', this.userId);
    
    // Get current tokens
    const userDoc = await getDoc(userRef);
    const currentTokens = userDoc.data()?.fcmTokens || [];
    
    // Check if this exact token already exists
    const existingIndex = currentTokens.findIndex(
      t => t.token === deviceInfo.token
    );
    
    if (existingIndex !== -1) {
      // Update existing token (refresh timestamp)
      console.log('🔄 Updating existing FCM token');
      currentTokens[existingIndex] = {
        ...deviceInfo,
        updatedAt: new Date().toISOString()
      };
    } else {
      // Add new token
      console.log('➕ Adding new FCM token');
      currentTokens.push({
        ...deviceInfo,
        addedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
    
    // Save to Firestore
    await updateDoc(userRef, {
      fcmTokens: currentTokens
    });
    
    console.log('✅ FCM token saved to Firestore');
    console.log('   Platform:', deviceInfo.platform);
    console.log('   Device ID:', deviceInfo.deviceId);
    console.log('   Total tokens:', currentTokens.length);
    
    return { success: true, tokenCount: currentTokens.length };
    
  } catch (error) {
    console.error('❌ Failed to save FCM token:', error);
    throw error;
  }
}


/**
 * Remove FCM token (called on logout or token invalidation)
 */
async removeFCMToken(token) {
  if (!this.userId) {
    console.warn('⚠️ No user ID for token removal');
    return;
  }

  try {
    const userRef = doc(this.db, 'users', this.userId);
    const userDoc = await getDoc(userRef);
    const currentTokens = userDoc.data()?.fcmTokens || [];
    
    // Remove the token
    const updatedTokens = currentTokens.filter(t => t.token !== token);
    
    if (updatedTokens.length === currentTokens.length) {
      console.log('⚠️ Token not found in Firestore');
      return { success: false, reason: 'token_not_found' };
    }
    
    await updateDoc(userRef, {
      fcmTokens: updatedTokens
    });
    
    console.log('✅ FCM token removed from Firestore');
    console.log('   Remaining tokens:', updatedTokens.length);
    
    return { success: true, remainingTokens: updatedTokens.length };
    
  } catch (error) {
    console.error('❌ Failed to remove FCM token:', error);
    throw error;
  }
}

/**
 * Get all FCM tokens for current user (for debugging)
 */
async getFCMTokens() {
  if (!this.userId) {
    console.warn('⚠️ No user ID for token retrieval');
    return [];
  }

  try {
    const userRef = doc(this.db, 'users', this.userId);
    const userDoc = await getDoc(userRef);
    
    const tokens = userDoc.data()?.fcmTokens || [];
    
    console.log('📱 Current FCM tokens:', tokens.length);
    tokens.forEach((t, i) => {
      console.log(`   ${i + 1}. ${t.platform} - ${t.deviceId}`);
    });
    
    return tokens;
    
  } catch (error) {
    console.error('❌ Failed to get FCM tokens:', error);
    return [];
  }
}

/**
 * Clean up old/invalid tokens (called periodically)
 */
async cleanupOldTokens(daysOld = 30) {
  if (!this.userId) return;

  try {
    const userRef = doc(this.db, 'users', this.userId);
    const userDoc = await getDoc(userRef);
    const currentTokens = userDoc.data()?.fcmTokens || [];
    
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);
    
    // Keep only tokens updated in last N days
    const validTokens = currentTokens.filter(t => {
      const tokenDate = new Date(t.updatedAt || t.addedAt);
      return tokenDate > cutoffDate;
    });
    
    if (validTokens.length < currentTokens.length) {
      await updateDoc(userRef, {
        fcmTokens: validTokens
      });
      
      const removed = currentTokens.length - validTokens.length;
      console.log(`🗑️ Removed ${removed} old FCM token(s)`);
    }
    
    return { success: true, removed: currentTokens.length - validTokens.length };
    
  } catch (error) {
    console.error('❌ Failed to cleanup old tokens:', error);
  }
}

// ============================================
// DAILY BRIEF NOTIFICATION LISTENERS
// ============================================


// Save conversation memory (important facts from conversation)
async initializeMemoryService() {
  try {
    if (!this.currentUser) {
      console.warn('❌ Cannot initialize memory service - no authenticated user');
      return null;
    }

    if (this.memoryService) {
      console.log('✅ Memory service already initialized');
      return this.memoryService;
    }

    console.log('🧠 Initializing Conversation Memory System...');
    
    // ✅ CRITICAL: Pass the Firestore service instance and userId
    this.memoryService = new ConversationMemorySystem(
      this,              // Pass the entire Firestore service
      this.currentUser.uid  // Pass userId
    );
    // ✅ CRITICAL: Wait for initialization to complete
    const success = await this.memoryService.initialize();
    
    if (success) {
      console.log('✅ Memory service initialized and ready');
      console.log('   User ID:', this.currentUser.uid);
      console.log('   Preptune ID:', this.preptuneUserId);
      console.log('   Is Ready:', this.memoryService.isReady());
    } else {
      console.error('❌ Memory service initialization failed');
      this.memoryService = null;
    }
    
    return this.memoryService;
  } catch (error) {
    console.error('❌ Failed to initialize memory service:', error);
    console.error('Error details:', error.message);
    this.memoryService = null;
    return null;
  }
}
// Get recent conversation memories
async getRecentMemories(limitCount = 10, daysBack = 7) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const memoryRef = collection(db, 'users', this.currentUser.uid, 'conversationMemory');
    
    // Get memories from last N days
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysBack);
    
    const q = query(
      memoryRef, 
      where('mentionedAt', '>=', cutoffDate),
      orderBy('mentionedAt', 'desc'), 
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    
    const memories = [];
    querySnapshot.forEach((doc) => {
      memories.push({
        id: doc.id,
        ...doc.data()
      });
    });

    console.log('Retrieved', memories.length, 'recent memories');
    return memories;
  } catch (error) {
    console.error('Failed to get recent memories:', error);
    return [];
  }
}

// Update memory reference (when AI mentions it)
async updateMemoryReference(memoryId) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const memoryRef = doc(db, 'users', this.currentUser.uid, 'conversationMemory', memoryId);
    
    await updateDoc(memoryRef, {
      lastReferencedAt: serverTimestamp(),
      referenceCount: increment(1)
    });
    
    console.log('Memory reference updated:', memoryId);
  } catch (error) {
    console.error('Failed to update memory reference:', error);
  }
}

// Search memories by topic/keyword
async searchMemories(searchTerm, limitCount = 5) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const memoryRef = collection(db, 'users', this.currentUser.uid, 'conversationMemory');
    
    // Search in tags
    const q = query(
      memoryRef,
      where('tags', 'array-contains', searchTerm.toLowerCase()),
      orderBy('mentionedAt', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    
    const memories = [];
    querySnapshot.forEach((doc) => {
      memories.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return memories;
  } catch (error) {
    console.error('Failed to search memories:', error);
    return [];
  }
}

// Get single document
async getDocument(path) {
  try {
    const docRef = doc(this.db, path);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        data: () => docSnap.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Failed to get document:', error);
    return null;
  }
}

// Get collection with query
async getCollection(path, options = {}) {
  try {
    const collectionRef = collection(this.db, path);
    let q = collectionRef;
    
    if (options.where) {
      const [field, operator, value] = options.where;
      q = query(q, where(field, operator, value));
    }
    
    if (options.orderBy) {
      q = query(q, orderBy(options.orderBy));
    }
    
    if (options.limit) {
      q = query(q, limit(options.limit));
    }
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      data: () => doc.data()
    }));
  } catch (error) {
    console.error('Failed to get collection:', error);
    return [];
  }
}

  // Initialize user data with proper new user handling
async initializeUserData() {
  if (!this.currentUser) {
    console.log('No currentUser, waiting for auth state...');
    await this.waitForAuthState(10000);
    
    if (!this.currentUser) {
      throw new Error('User must be authenticated');
    }
  }

  try {
    console.log('Initializing user data for:', this.currentUser.email);
    
    const userDocRef = doc(db, 'users', this.currentUser.uid);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      console.log('🆕 NEW USER - Creating fresh document');
      const newUserData = await this.createNewUserDocument();
      this.isInitialized = true;
      this.userId = this.currentUser.uid;
      console.log(`✅ New user initialized: ${newUserData.settings.userName}`);
    } else {
      console.log('👤 EXISTING USER - Loading data');
      const userData = userDocSnap.data();
      this.preptuneUserId = userData.preptuneUserId;
      this.userId = this.currentUser.uid;

      await updateDoc(userDocRef, {
        lastActiveAt: serverTimestamp()
      });
      
      this.isInitialized = true;
      console.log(`✅ Existing user loaded: ${userData.settings?.userName || 'Unknown'}`);
    }

    // ============ ADD THIS SECTION ============
    // Initialize memory service AFTER user is verified
    if (!this.memoryService) {
      await this.initializeMemoryService();
    }
    // ==========================================

  } catch (error) {
    console.error('❌ Failed to initialize user data:', error);
    throw error;
  }
}

/**
 * Get the memory service instance
 */
getMemoryService() {
  if (!this.memoryService) {
    console.warn('⚠️ Memory service not initialized yet');
    return null;
  }
  return this.memoryService;
}

/**
 * Check if memory service is ready
 */
isMemoryServiceReady() {
  return !!this.memoryService;
}
  // Generate truly unique Preptune ID
  generatePreptuneId() {
    const timestamp = Date.now();
    const random1 = Math.random().toString(36).substring(2, 15);
    const random2 = Math.random().toString(36).substring(2, 15);
    const userIdPart = this.currentUser.uid.substring(0, 8);
    
    return `preptune_${userIdPart}_${timestamp}_${random1}${random2}`;
  }

  // Get user data with better error handling
  async getUserData() {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    if (!this.isInitialized) {
      await this.initializeUserData();
    }

    const userDocRef = doc(db, 'users', this.currentUser.uid);
    const userDocSnap = await getDoc(userDocRef);
    
    if (!userDocSnap.exists()) {
      console.error('User document not found, creating new one...');
      await this.createNewUserDocument();
      return await this.getUserData();
    }

    return userDocSnap.data();
  }

  // Get trial status for current user
  async getTrialStatus() {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      const userData = await this.getUserData();
      return this.calculateTrialStatus(userData);
    } catch (error) {
      console.error('Failed to get trial status:', error);
      throw error;
    }
  }

  async syncSignupCount() {
  if (!this.isAdmin()) {
    throw new Error('Only administrators can sync signup count');
  }

  try {
    console.log('Syncing signup count with actual users...');
    
    // Get actual user count (excluding admins)
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    const adminEmailsLower = this.adminEmails.map(email => email.toLowerCase());
    let actualBetaUserCount = 0;
    
    // Count only non-admin users
    snapshot.forEach((doc) => {
      const userData = doc.data();
      const userEmail = userData.email?.toLowerCase();
      
      if (userEmail && !adminEmailsLower.includes(userEmail)) {
        actualBetaUserCount++;
      }
    });
    
    console.log('Actual beta user count:', actualBetaUserCount);
    
    // Update the signup capacity document
    const capacityRef = doc(db, 'signupCapacity', 'betaLimits');
    
    await updateDoc(capacityRef, {
      currentSignups: actualBetaUserCount,
      lastUpdated: serverTimestamp(),
      updatedBy: this.currentUser.email,
      lastSyncAt: serverTimestamp(),
      syncNote: `Synced with actual user count: ${actualBetaUserCount} users`
    });

    await this.logAdminAction({
      action: 'signup_count_synced',
      adminUserId: this.currentUser.uid,
      performedBy: this.currentUser.email,
      actualUserCount: actualBetaUserCount,
      syncedAt: new Date().toISOString()
    });

    console.log('Signup count synced successfully to:', actualBetaUserCount);
    
    return {
      success: true,
      syncedCount: actualBetaUserCount,
      message: `Signup count synchronized to ${actualBetaUserCount} actual users`
    };
    
  } catch (error) {
    console.error('Failed to sync signup count:', error);
    throw error;
  }
}
  // Check if user can start a session
  async canStartSession() {
    try {
      // Admin users can always start sessions
      if (this.isAdmin()) {
        return {
          canStart: true,
          trialStatus: {
            isAdmin: true,
            daysLeft: Infinity,
            minutesLeft: Infinity
          }
        };
      }

      const trialStatus = await this.getTrialStatus();
      
      if (trialStatus.isExpired) {
        return {
          canStart: false,
          reason: trialStatus.daysLeft <= 0 ? 'trial_expired' : 'minutes_exhausted',
          trialStatus
        };
      }

      return {
        canStart: true,
        trialStatus
      };
    } catch (error) {
      console.error('Failed to check session eligibility:', error);
      return {
        canStart: false,
        reason: 'error',
        error: error.message
      };
    }
  }

  // Start a session and begin tracking usage
  async startSession() {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      const sessionCheck = await this.canStartSession();
      
      if (!sessionCheck.canStart) {
        throw new Error(`Cannot start session: ${sessionCheck.reason}`);
      }

      const userDocRef = doc(db, 'users', this.currentUser.uid);
      const sessionStartTime = new Date();
      
      await updateDoc(userDocRef, {
        'callTimer.isActive': true,
        'callTimer.sessionStartTime': sessionStartTime,
        'callTimer.lastPaused': null,
        lastActiveAt: serverTimestamp()
      });

      console.log('Session started successfully');
      return {
        success: true,
        sessionStartTime,
        trialStatus: sessionCheck.trialStatus
      };
    } catch (error) {
      console.error('Failed to start session:', error);
      throw error;
    }
  }

  // FIXED: End session and properly update remaining time
  async endSession() {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      const userData = await this.getUserData();
      const sessionStartTime = userData.callTimer?.sessionStartTime;
      
      if (!sessionStartTime) {
        console.log('No active session to end');
        return { success: false, reason: 'no_active_session' };
      }

      const sessionEnd = new Date();
      const sessionStart = sessionStartTime.toDate ? sessionStartTime.toDate() : new Date(sessionStartTime);
      const sessionDurationSeconds = Math.floor((sessionEnd - sessionStart) / 1000);
      const sessionDurationMinutes = sessionDurationSeconds / 60;

      // Get current usage and remaining time
      const currentMinutesUsed = userData.trialUsage?.totalMinutesUsed || 0;
      const currentSecondsUsed = userData.trialUsage?.totalSecondsUsed || 0;
      const currentRemainingTime = userData.callTimer?.remainingTime || 0;
      const sessionsCount = userData.trialUsage?.sessionsCount || 0;

      // Update usage
      const newTotalMinutesUsed = currentMinutesUsed + sessionDurationMinutes;
      const newTotalSecondsUsed = currentSecondsUsed + sessionDurationSeconds;

      // FIXED: Update remaining time by subtracting session duration
      const newRemainingTime = Math.max(0, currentRemainingTime - sessionDurationSeconds);

      // Get today's date for daily tracking
      const today = sessionEnd.toISOString().split('T')[0];
      const dailyUsage = userData.trialUsage?.dailyUsage || {};
      const todayUsage = dailyUsage[today] || 0;

      const userDocRef = doc(db, 'users', this.currentUser.uid);
      
      await updateDoc(userDocRef, {
        'callTimer.isActive': false,
        'callTimer.sessionStartTime': null,
        'callTimer.lastPaused': serverTimestamp(),
        'callTimer.remainingTime': newRemainingTime, // FIXED: Properly subtract session duration
        'trialUsage.totalMinutesUsed': newTotalMinutesUsed,
        'trialUsage.totalSecondsUsed': newTotalSecondsUsed,
        'trialUsage.sessionsCount': sessionsCount + 1,
        'trialUsage.lastSessionDuration': sessionDurationSeconds,
        'trialUsage.lastUsedAt': serverTimestamp(),
        [`trialUsage.dailyUsage.${today}`]: todayUsage + sessionDurationMinutes,
        lastActiveAt: serverTimestamp()
      });

      console.log(`Session ended: ${sessionDurationSeconds}s (${sessionDurationMinutes.toFixed(2)} minutes)`);
      console.log(`Total usage: ${newTotalMinutesUsed.toFixed(2)}/${this.TOTAL_TRIAL_MINUTES} minutes`);
      console.log(`Remaining time: ${newRemainingTime}s`);

      return {
        success: true,
        sessionDurationSeconds,
        sessionDurationMinutes,
        totalMinutesUsed: newTotalMinutesUsed,
        minutesRemaining: Math.max(0, newRemainingTime / 60),
        remainingTimeSeconds: newRemainingTime
      };
    } catch (error) {
      console.error('Failed to end session:', error);
      throw error;
    }
  }

  // Update user data with validation
  async updateUserData(updates) {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    if (!this.isInitialized) {
      await this.initializeUserData();
    }

    const userDocRef = doc(db, 'users', this.currentUser.uid);
    
    try {
      await updateDoc(userDocRef, {
        ...updates,
        lastActiveAt: serverTimestamp()
      });
      
      console.log('User data updated:', Object.keys(updates));
    } catch (error) {
      if (error.code === 'not-found') {
        console.log('User document not found, creating...');
        await this.createNewUserDocument();
        await updateDoc(userDocRef, {
          ...updates,
          lastActiveAt: serverTimestamp()
        });
      } else {
        throw error;
      }
    }
  }

  // Get user's Preptune ID
  getPreptuneUserId() {
    if (!this.isInitialized || !this.currentUser) {
      console.warn('Service not initialized or no user, returning null');
      return null;
    }
    return this.preptuneUserId;
  }

  // Enhanced isReady method with detailed logging
isReady() {
  const hasUser = !!this.currentUser;
  const hasUserId = !!this.preptuneUserId;
  const isInit = this.isInitialized;
  const authReady = this.authStateReady;
  
  // ✅ Return silently - no console spam
  return hasUser && hasUserId && isInit && authReady;
}


  // Wait for service to be ready
  async waitForReady(timeoutMs = 30000) {
    const startTime = Date.now();
    
    while (!this.isReady() && (Date.now() - startTime) < timeoutMs) {
      console.log('Waiting for service to be ready...');
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    if (!this.isReady()) {
      throw new Error('Service failed to become ready within timeout');
    }
    
    console.log('Service is now ready');
    return true;
  }

  // Force initialization check
  async ensureInitialized() {
    if (!this.authStateReady) {
      await this.waitForAuthState();
    }

    if (!this.isReady()) {
      console.log('Service not ready, attempting initialization...');
      
      if (this.currentUser) {
        if (!this.isInitialized) {
          await this.initializeUserData();
        }
      } else {
        throw new Error('No authenticated user available');
      }
    }
    
    return this.isReady();
  }

  // Get current user info with trial status
  getCurrentUserInfo() {
    if (!this.currentUser) {
      return null;
    }
    
    return {
      email: this.currentUser.email,
      uid: this.currentUser.uid,
      preptuneUserId: this.preptuneUserId,
      isInitialized: this.isInitialized
    };
  }
  // Add these methods to your FirestorePersistentStorageService class

/**
 * Get all chat sessions for the current user
 */
async getAllChatSessions(limitCount = 100) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const sessionsRef = collection(db, 'users', this.currentUser.uid, 'chatSessions');
    const q = query(
      sessionsRef,
      orderBy('lastUpdated', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const sessions = [];

    querySnapshot.forEach((doc) => {
      sessions.push({
        id: doc.id,
        ...doc.data()
      });
    });

    console.log('✅ Retrieved', sessions.length, 'chat sessions');
    return sessions;
  } catch (error) {
    console.error('❌ Failed to get chat sessions:', error);
    return [];
  }
}

// ============================================
// FIXED DELETION METHODS FOR FirestorePersistentStorageService
// Add these to replace your existing deletion methods
// ============================================

/**
 * ✅ FIXED: Delete a single chat session
 * Uses the correct path: users/{userId}/conversations/{sessionId}
 */
async deleteChatSession(sessionId) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  if (!sessionId || typeof sessionId !== 'string') {
    throw new Error('Invalid session ID');
  }

  try {
    console.log('🗑️ Deleting chat session:', sessionId);

    // ✅ CORRECT PATH - matches where conversations are saved
    const sessionRef = doc(
      db,
      'users',
      this.currentUser.uid,
      'conversations',  // ✅ This is the correct collection name
      sessionId
    );

    // ✅ Delete the document
    await deleteDoc(sessionRef);

    console.log('✅ Session deleted successfully:', sessionId);
    
    // ✅ Log the action for audit trail
    await this.logAdminAction({
      action: 'chat_session_deleted',
      sessionId: sessionId,
      deletedBy: this.currentUser.email,
      deletedAt: new Date().toISOString()
    }).catch(err => console.warn('Failed to log deletion:', err));

    return { success: true, sessionId };
  } catch (error) {
    console.error('❌ Failed to delete chat session:', error);
    console.error('   Session ID:', sessionId);
    console.error('   Error code:', error.code);
    console.error('   Error message:', error.message);
    throw error;
  }
}

/**
 * ✅ FIXED: Delete multiple chat sessions (batch delete)
 * Used for "Clear All" functionality
 */
async deleteChatSessions(sessionIds) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  if (!Array.isArray(sessionIds) || sessionIds.length === 0) {
    throw new Error('Invalid session IDs array');
  }

  try {
    console.log(`🗑️ Batch deleting ${sessionIds.length} sessions...`);

    // ✅ Use batched writes for efficiency (max 500 per batch)
    const batchSize = 500;
    const batches = [];

    for (let i = 0; i < sessionIds.length; i += batchSize) {
      const batch = writeBatch(db);
      const batchIds = sessionIds.slice(i, i + batchSize);

      batchIds.forEach(sessionId => {
        const sessionRef = doc(
          db,
          'users',
          this.currentUser.uid,
          'conversations',
          sessionId
        );
        batch.delete(sessionRef);
      });

      batches.push(batch.commit());
    }

    // ✅ Wait for all batches to complete
    await Promise.all(batches);

    console.log(`✅ Successfully deleted ${sessionIds.length} sessions`);

    // ✅ Log the action
    await this.logAdminAction({
      action: 'batch_chat_sessions_deleted',
      sessionCount: sessionIds.length,
      deletedBy: this.currentUser.email,
      deletedAt: new Date().toISOString()
    }).catch(err => console.warn('Failed to log batch deletion:', err));

    return { 
      success: true, 
      deletedCount: sessionIds.length,
      sessionIds 
    };
  } catch (error) {
    console.error('❌ Failed to batch delete sessions:', error);
    console.error('   Count:', sessionIds.length);
    console.error('   Error code:', error.code);
    throw error;
  }
}

/**
 * ✅ FIXED: Delete sessions by date range (month/week)
 * This is what "Clear Month" or "Clear Week" should use
 */
async deleteChatSessionsByDateRange(startDate, endDate, label = '') {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    console.log(`🗑️ Deleting sessions from ${startDate} to ${endDate}...`);

    // ✅ Get all sessions in the date range
    const conversationsRef = collection(
      db,
      'users',
      this.currentUser.uid,
      'conversations'
    );

    // ✅ Query by date range
    const q = query(
      conversationsRef,
      where('lastUpdated', '>=', startDate),
      where('lastUpdated', '<=', endDate)
    );

    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      console.log('📭 No sessions found in date range');
      return { success: true, deletedCount: 0 };
    }

    // ✅ Extract session IDs
    const sessionIds = snapshot.docs.map(doc => doc.id);

    console.log(`📊 Found ${sessionIds.length} sessions in range`);

    // ✅ Use batch delete
    const result = await this.deleteChatSessions(sessionIds);

    console.log(`✅ Deleted ${result.deletedCount} sessions from ${label || 'date range'}`);

    return result;
  } catch (error) {
    console.error('❌ Failed to delete sessions by date range:', error);
    throw error;
  }
}

/**
 * ✅ HELPER: Convert month/week to date range
 */
getDateRangeForPeriod(monthYear, weekLabel = null) {
  try {
    // Parse month and year
    const [monthName, year] = monthYear.split(' ');
    const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();
    const yearNum = parseInt(year);

    if (weekLabel) {
      // Get specific week range
      const weekNum = parseInt(weekLabel.split(' ')[1]);
      
      // Calculate start of week
      const firstDayOfMonth = new Date(yearNum, monthIndex, 1);
      const firstDayOfWeek = firstDayOfMonth.getDay();
      const daysOffset = (weekNum - 1) * 7 - firstDayOfWeek + 1;
      
      const startDate = new Date(yearNum, monthIndex, 1 + daysOffset);
      const endDate = new Date(yearNum, monthIndex, 1 + daysOffset + 6, 23, 59, 59, 999);

      return { startDate, endDate };
    } else {
      // Get entire month range
      const startDate = new Date(yearNum, monthIndex, 1, 0, 0, 0, 0);
      const endDate = new Date(yearNum, monthIndex + 1, 0, 23, 59, 59, 999);

      return { startDate, endDate };
    }
  } catch (error) {
    console.error('Failed to parse date range:', error);
    throw new Error('Invalid date format');
  }
}

/**
 * ✅ FIXED: Clear all conversations
 * This is the nuclear option - deletes EVERYTHING
 */
async clearAllConversations() {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    console.log('🗑️ Clearing ALL conversations...');

    const conversationsRef = collection(
      db,
      'users',
      this.currentUser.uid,
      'conversations'
    );

    // ✅ Get all sessions
    const snapshot = await getDocs(conversationsRef);

    if (snapshot.empty) {
      console.log('📭 No conversations to clear');
      return { success: true, deletedCount: 0 };
    }

    const sessionIds = snapshot.docs.map(doc => doc.id);

    console.log(`📊 Found ${sessionIds.length} total conversations`);

    // ✅ Batch delete all
    const result = await this.deleteChatSessions(sessionIds);

    console.log(`✅ Cleared all ${result.deletedCount} conversations`);

    return result;
  } catch (error) {
    console.error('❌ Failed to clear all conversations:', error);
    throw error;
  }
}




/**
 * Update chat session metadata (for pinning, etc.)
 */
async updateChatSessionMetadata(sessionId, updates) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    // ✅ FIXED: Use 'conversations' to match getAllChatSessions
    const sessionRef = doc(db, 'users', this.currentUser.uid, 'conversations', sessionId);
    
    // ✅ Use setDoc with merge to create if doesn't exist
    await setDoc(sessionRef, {
      ...updates,
      lastUpdated: serverTimestamp()
    }, { merge: true });
    
    console.log('✅ Chat session metadata updated:', sessionId);
    return { success: true };
  } catch (error) {
    console.error('❌ Failed to update chat session metadata:', error);
    throw error;
  }
}

/**
 * Save or update a chat conversation
 */
async saveConversation(conversationData) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const sessionRef = doc(db, 'users', this.currentUser.uid, 'chatSessions', conversationData.id);
    
    await setDoc(sessionRef, {
      ...conversationData,
      userId: this.currentUser.uid,
      lastUpdated: serverTimestamp()
    }, { merge: true });

    console.log('✅ Conversation saved:', conversationData.id);
    return { success: true, sessionId: conversationData.id };
  } catch (error) {
    console.error('❌ Failed to save conversation:', error);
    throw error;
  }
}
/**
 * ✅ Get the latest active chat session
 */
async getLatestChatSession() {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    console.log('🔍 Getting latest chat session...');

    const conversationsRef = collection(
      db,
      'users',
      this.currentUser.uid,
      'conversations'
    );

    const q = query(
      conversationsRef,
      orderBy('lastUpdated', 'desc'),
      limit(1)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.log('📭 No previous sessions found');
      return null;
    }

    const doc = snapshot.docs[0];
    const data = doc.data();

    console.log('✅ Latest session found:', {
      id: doc.id,
      messageCount: data.messages?.length || 0,
      preview: data.preview
    });

    return {
      id: doc.id,
      ...data
    };

  } catch (error) {
    console.error('❌ Failed to get latest session:', error);
    return null;
  }
}
  /**
 * ✅ FIXED: Get a specific chat session
 * This now uses the exact same query logic as getAllChatSessions
 */
async getSavedChatSession(sessionId) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    // ✅ VALIDATE INPUT
    if (!sessionId || typeof sessionId !== 'string') {
      console.error('❌ Invalid sessionId:', sessionId);
      throw new Error(`Invalid sessionId: ${sessionId}`);
    }

    console.log('🔍 Getting session:', {
      sessionId: sessionId,
      userId: this.currentUser.uid,
      path: `users/${this.currentUser.uid}/conversations/${sessionId}`
    });

    // ✅ METHOD 1: Direct document access (Fast)
    const conversationRef = doc(
      db,
      'users',
      this.currentUser.uid,
      'conversations',
      sessionId
    );

    const docSnap = await getDoc(conversationRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      console.log('✅ Session found via direct access:', {
        sessionId: sessionId,
        messageCount: data.messages?.length || 0,
        preview: data.preview
      });

      return {
        id: sessionId,
        ...data
      };
    }

    // ✅ METHOD 2: Query all and find (Fallback)
    console.log('⚠️ Direct access failed, trying query method...');

    const conversationsRef = collection(
      db,
      'users',
      this.currentUser.uid,
      'conversations'
    );

    const q = query(conversationsRef);
    const snapshot = await getDocs(q);

    console.log('📊 Query returned', snapshot.size, 'documents');

    for (const doc of snapshot.docs) {
      if (doc.id === sessionId) {
        const data = doc.data();
        console.log('✅ Session found via query:', {
          sessionId: sessionId,
          messageCount: data.messages?.length || 0,
          preview: data.preview
        });

        return {
          id: sessionId,
          ...data
        };
      }
    }

    // Not found
    console.warn('⚠️ Conversation not found:', {
      sessionId: sessionId,
      totalDocumentsQueried: snapshot.size
    });

    return null;

  } catch (error) {
    console.error('❌ Error getting chat session:', {
      sessionId: sessionId,
      errorMessage: error.message,
      errorCode: error.code
    });
    throw error;
  }
}

/**
 * ✅ ALTERNATIVE: Use the allSessions from memory
 * This is MUCH faster - we already loaded them!
 */
async getSessionFromMemory(sessionId, allSessionsArray) {
  if (!allSessionsArray || allSessionsArray.length === 0) {
    console.warn('⚠️ allSessions is empty');
    return null;
  }

  const session = allSessionsArray.find(s => s.id === sessionId);

  if (session) {
    console.log('✅ Session found in memory:', {
      sessionId: sessionId,
      messageCount: session.messages?.length || 0,
      preview: session.preview
    });
    return session;
  }

  console.warn('⚠️ Session not found in memory:', sessionId);
  return null;
}
/**
 * ✅ CRITICAL FIX: Save conversation with proper validation
 * This MUST work 100% or chats won't be retrievable
 */
async saveConversation(sessionData) {
  if (!this.currentUser || !this.preptuneUserId) {
    console.error('❌ CRITICAL: Cannot save - user not authenticated');
    console.error('   currentUser:', !!this.currentUser);
    console.error('   preptuneUserId:', !!this.preptuneUserId);
    throw new Error('User not authenticated or no Preptune ID');
  }

  try {
    // ✅ VALIDATE INPUT
    if (!sessionData) {
      throw new Error('sessionData is required');
    }

    const sessionId = sessionData.id;
    if (!sessionId || typeof sessionId !== 'string') {
      throw new Error(`Invalid sessionId: ${sessionId}. Must be a non-empty string`);
    }

    if (!sessionData.messages || !Array.isArray(sessionData.messages)) {
      throw new Error('messages must be an array');
    }

    if (sessionData.messages.length === 0) {
      console.warn('⚠️ Saving conversation with no messages:', sessionId);
    }

    // ✅ SANITIZE MESSAGES
    const sanitizedMessages = sessionData.messages.map(msg => ({
      id: msg.id,
      type: msg.type,
      content: msg.content || '',
      timestamp: msg.timestamp || new Date().toISOString(),
      // Don't save large binary data or functions
      ...(msg.audioUrl && { audioUrl: msg.audioUrl }),
      ...(msg.dialogue && { dialogue: msg.dialogue })
    }));

    // ✅ PREPARE FIRESTORE DOCUMENT
    const docData = {
      id: sessionId,
      userId: this.currentUser.uid,
      preptuneUserId: this.preptuneUserId,
      
      // ✅ CRITICAL: Use Firestore serverTimestamp
      savedAt: serverTimestamp(),
      lastUpdated: serverTimestamp(),
      
      // Session content
      messages: sanitizedMessages,
      preview: sessionData.preview || 'Chat',
      messageCount: sanitizedMessages.length,
      
      // Metadata
      tag: sessionData.tag || 'General',
      timestamp: sessionData.timestamp || new Date().toISOString(),
      
      // Flags
      pinned: sessionData.pinned || false,
      favorited: sessionData.favorited || false
    };

    // ✅ LOG BEFORE SAVE
    console.log('💾 Saving conversation to Firestore:', {
      sessionId: sessionId,
      userId: this.currentUser.uid,
      messageCount: sanitizedMessages.length,
      preview: docData.preview,
      path: `users/${this.currentUser.uid}/conversations/${sessionId}`
    });

    // ✅ SAVE WITH PROPER PATH
    const conversationRef = doc(
      db,
      'users',
      this.currentUser.uid,
      'conversations',
      sessionId
    );

    // ✅ Use setDoc with merge:true to avoid overwriting
    await setDoc(conversationRef, docData, { merge: true });

    console.log('✅ Conversation saved successfully:', {
      sessionId: sessionId,
      messages: sanitizedMessages.length,
      timestamp: new Date().toISOString()
    });

    // ✅ VERIFY SAVE (optional but recommended)
    const verification = await getDoc(conversationRef);
    if (!verification.exists()) {
      console.error('❌ VERIFICATION FAILED: Document was not saved!');
      throw new Error('Document save verification failed');
    }

    console.log('✅ VERIFICATION: Document exists in Firestore');

    return {
      sessionId: sessionId,
      ...docData
    };

  } catch (error) {
    console.error('❌ CRITICAL: Failed to save conversation:', error);
    console.error('   Error message:', error.message);
    console.error('   Error code:', error.code);
    console.error('   Stack:', error.stack);
    throw error;
  }
}

/**
 * ✅ NEW: Get all chat sessions with better error handling
 */
async getAllChatSessions(limitCount = 100) {
  if (!this.currentUser) {
    console.error('❌ No authenticated user');
    throw new Error('User not authenticated');
  }

  try {
    console.log('📂 Fetching all chat sessions for user:', this.currentUser.uid);

    const conversationsRef = collection(
      db,
      'users',
      this.currentUser.uid,
      'conversations'
    );

    // ✅ QUERY WITH TIMEOUT
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Firestore query timeout')), 10000)
    );

    const queryPromise = (async () => {
      const q = query(
        conversationsRef,
        orderBy('lastUpdated', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);

      console.log('📊 Query returned:', snapshot.size, 'documents');

      const sessions = [];
      const seenIds = new Set();

      snapshot.forEach((doc) => {
        const sessionId = doc.id;

        // ✅ SKIP DUPLICATES
        if (seenIds.has(sessionId)) {
          console.warn('⚠️ Duplicate session ID skipped:', sessionId);
          return;
        }

        seenIds.add(sessionId);

        const data = doc.data();

        // ✅ VALIDATE DATA
        if (!data.messages || !Array.isArray(data.messages)) {
          console.warn('⚠️ Invalid session structure:', sessionId);
          return;
        }

        sessions.push({
          id: sessionId,
          ...data
        });
      });

      return sessions;
    })();

    const sessions = await Promise.race([queryPromise, timeoutPromise]);

    console.log(`✅ Loaded ${sessions.length} unique sessions`);
    return sessions;

  } catch (error) {
    console.error('❌ Failed to get all chat sessions:', error);
    console.error('   Error code:', error.code);
    console.error('   Message:', error.message);
    return []; // Return empty array instead of throwing
  }
}


  // Get latest conversation
  async getLatestConversation() {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      const conversationsRef = collection(db, 'users', this.currentUser.uid, 'conversations');
      const q = query(conversationsRef, orderBy('savedAt', 'desc'), limit(1));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data()
        };
      }
      
      return null;
    } catch (error) {
      console.error('Failed to get latest conversation:', error);
      return null;
    }
  }
// ============================================
// ADD THESE METHODS TO FirestorePersistentStorageService CLASS
// (Remove the podcastService delegation and add these directly)
// ============================================
// ===== PODCAST MANAGEMENT =====
  /**
   * ✅ Save voice preferences (using Hume AI voice IDs)
   */
  async saveVoicePreferences(voiceMap) {
    try {
      if (!this.currentUser) throw new Error('User not authenticated');
      
      const userRef = doc(db, 'users', this.currentUser.uid);
      
      await updateDoc(userRef, {
        'voicePreferences': voiceMap,
        'voicePreferences.updatedAt': serverTimestamp()
      });
      
      console.log('✅ Voice preferences saved:', voiceMap);
      return voiceMap;
    } catch (error) {
      console.error('❌ Failed to save voice preferences:', error);
      throw error;
    }
  }
 /**
   * ✅ Get voice preferences
   */
  async getVoicePreferences() {
    try {
      if (!this.currentUser) throw new Error('User not authenticated');
      
      const userRef = doc(db, 'users', this.currentUser.uid);
      const docSnap = await getDoc(userRef);
      
      if (docSnap.exists()) {
        const prefs = docSnap.data().voicePreferences;
        
        // Return default if none saved
        if (!prefs) {
          return this.getDefaultVoiceMap();
        }
        
        console.log('✅ Voice preferences loaded:', prefs);
        return prefs;
      }
      
      return this.getDefaultVoiceMap();
    } catch (error) {
      console.error('❌ Failed to load voice preferences:', error);
      return this.getDefaultVoiceMap();
    }
  }
  getDefaultVoiceMap() {
    return {
      'Host 1': {
        voice: {
          id: "33045fd9-8010-43f6-b6b0-da3fbf326c29" // Emma (female)
        }
      },
      'Host 2': {
        voice: {
          id: "41745808-6790-4be4-83db-363e20db7e71" // James (male)
        }
      },
      'Host 3': {
        voice: {
          id: "43e411b3-b2cc-40da-b742-4abf0e3557b2" // Jackie (female)
        }
      },
      'Host 4': {
        voice: {
          id: "b152864b-6720-496a-9d18-eaadb31516ee" // Kenny (male)
        }
      }
    };
  }

listenToDailyBriefStatus(callback) {
  if (!this.currentUser) {
    console.warn('No user logged in');
    return () => {};
  }

  const userId = this.currentUser.uid;
  const userRef = doc(db, 'users', userId);
  
  const unsubscribe = onSnapshot(userRef, (docSnap) => {
    if (docSnap.exists()) {
      const status = docSnap.data()?.dailyBriefCatchUp?.status;
      callback(status);
    } else {
      callback(null);
    }
  }, (error) => {
    console.error('Error listening to brief status:', error);
    callback(null);
  });

  return unsubscribe;
}

// ============================================
// STATION SESSION MANAGEMENT
// ============================================

/**
 * Save live station session for resume capability
 */
async saveStationSession(
  lineIndex, 
  newsIndex, 
  totalLines, 
  totalNewsCount, 
  companyTicker,
  newsCards = [],
  preGeneratedIndices = [],
  currentCardIndex = 0
) {
  if (!this.currentUser) {
    console.log('⚠️ No user for session save');
    return;
  }

  try {
    const sessionRef = doc(
      db,
      'users',
      this.currentUser.uid,
      'stationSessions',
      companyTicker
    );

    const sessionData = {
      startedAt: new Date().toISOString(),
      lastPausedAt: new Date().toISOString(),
      lastLineIndex: lineIndex,
      currentNewsIndex: newsIndex,
      company: companyTicker,
      date: new Date().toDateString(),
      totalLines: totalLines,
      totalNewsCount: totalNewsCount,
      isComplete: false,
      newsCards: newsCards,
      preGeneratedIndices: preGeneratedIndices,
      currentCardIndex: currentCardIndex,
      updatedAt: serverTimestamp()
    };

    await setDoc(sessionRef, sessionData, { merge: true });
    console.log(`✅ Session saved to Firebase: line ${lineIndex}/${totalLines}, card ${currentCardIndex}`);
  } catch (error) {
    console.log(`⚠️ Could not save session to Firebase: ${error.message}`);
  }
}
// Add these methods to the class

async saveFeedback(feedbackData) {
  if (!this.currentUser) {
    console.warn('⚠️ No user for feedback save');
    return;
  }

  try {
    const feedbackRef = collection(db, 'users', this.currentUser.uid, 'newsFeedback');
    
    await addDoc(feedbackRef, {
      storyId: feedbackData.cardId,
      sourceId: feedbackData.sourceId,
      feedback: feedbackData.feedback,
      topic: feedbackData.topic,
      storyTitle: feedbackData.storyTitle,
      savedAt: serverTimestamp(),
      timestamp: feedbackData.timestamp
    });
    
    console.log('✅ Feedback saved:', feedbackData.topic, feedbackData.feedback);
  } catch (error) {
    console.error('❌ Failed to save feedback:', error);
  }
}

async getTopicScores() {
  if (!this.currentUser) return {};
  
  try {
    const feedbackRef = collection(db, 'users', this.currentUser.uid, 'newsFeedback');
    const querySnapshot = await getDocs(feedbackRef);
    
    const scores = {};
    
    querySnapshot.forEach(doc => {
      const data = doc.data();
      const topic = data.topic;
      
      if (!scores[topic]) {
        scores[topic] = { likes: 0, dislikes: 0 };
      }
      
      if (data.feedback === 'up') scores[topic].likes++;
      if (data.feedback === 'down') scores[topic].dislikes++;
    });
    
    console.log('📊 Topic scores:', scores);
    return scores;
  } catch (error) {
    console.error('❌ Failed to get scores:', error);
    return {};
  }
}
/**
 * Get live station session for resume
 */
async getStationSession(companyTicker) {
  if (!this.currentUser) {
    console.log('⚠️ No user for session load');
    return null;
  }

  try {
    const sessionRef = doc(
      db,
      'users',
      this.currentUser.uid,
      'stationSessions',
      companyTicker
    );
    const sessionSnap = await getDoc(sessionRef);

    if (sessionSnap.exists()) {
      const data = sessionSnap.data();
      console.log(`📍 Found session: line ${data.lastLineIndex}/${data.totalLines}`);
      return data;
    }

    return null;
  } catch (error) {
    console.log(`⚠️ Could not load session from Firebase: ${error.message}`);
    return null;
  }
}
// Save the full script + segments
async saveStationScript(ticker, scriptText, segments, newsMetadata) {
  if (!this.currentUser) {
    console.log('⚠️ No user for script save');
    return;
  }
  
  try {
    // ✅ Save under user's subcollection instead of root
    const sessionRef = doc(
      db,
      'users',
      this.currentUser.uid,
      'stationScripts',
      ticker
    );
    
    await setDoc(sessionRef, {
      ticker,
      scriptText,
      segments,
      newsMetadata,
      createdAt: new Date().toISOString(),
      date: new Date().toDateString()
    });
    console.log(`✅ Script saved for ${ticker}`);
  } catch (error) {
    console.error('❌ Error saving script:', error);
  }
}

// Retrieve the script
// REPLACE WITH:
async getStationScript(ticker) {
  if (!this.currentUser) {
    console.log('⚠️ No user for script load');
    return null;
  }
  
  try {
    // ✅ Get from user's subcollection
    const sessionRef = doc(
      db,
      'users',
      this.currentUser.uid,
      'stationScripts',
      ticker
    );
    const sessionSnap = await getDoc(sessionRef);
    
    if (sessionSnap.exists()) {
      const data = sessionSnap.data();
      // Check if it's from today
      if (data.date === new Date().toDateString()) {
        console.log(`✅ Script retrieved for ${ticker}`);
        return data;
      }
    }
    return null;
  } catch (error) {
    console.error('❌ Error getting script:', error);
    return null;
  }
}

// Clear script when done
async clearStationScript(ticker) {
  if (!this.currentUser) return;
  
  try {
    // ✅ Clear from user's subcollection
    const sessionRef = doc(
      db,
      'users',
      this.currentUser.uid,
      'stationScripts',
      ticker
    );
    await deleteDoc(sessionRef);
    console.log(`🗑️ Script cleared for ${ticker}`);
  } catch (error) {
    console.error('❌ Error clearing script:', error);
  }
}
/**
 * Clear live station session
 */
async clearStationSession(companyTicker) {
  if (!this.currentUser) return;

  try {
    const sessionRef = doc(
      db,
      'users',
      this.currentUser.uid,
      'stationSessions',
      companyTicker
    );
    await deleteDoc(sessionRef);
    console.log('🗑️ Session cleared from Firebase');
  } catch (error) {
    console.log(`⚠️ Could not clear session: ${error.message}`);
  }
}
// ✅ CORRECT: Include userId in the path
async deletePodcast(podcastId) {
  try {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    // ✅ CORRECT PATH - Match the save path
    const podcastRef = doc(
      db,
      'users',              // ✅ Changed from 'InnerloopPodcast'
      this.currentUser.uid,
      'savedPodcasts', 
      podcastId
    );

    await deleteDoc(podcastRef);
    console.log('✅ Podcast deleted:', podcastId);
    
    return true;
  } catch (error) {
    console.error('❌ Failed to delete podcast:', error);
    throw error;
  }
}
// ✅ CORRECT VERSION - Use imported db, not this.db
// In FirestorePersistentStorageService.jsx
// ============================================
// DUPLICATE PREVENTION METHODS
// Add these to FirestorePersistentStorageService.jsx
// ============================================

/**
 * ✅ Check if article already saved by URL
 */
async isArticleAlreadySaved(articleUrl) {
  if (!this.currentUser) return false;

  try {
    const articleRef = collection(db, 'users', this.currentUser.uid, 'savedArticles');
    const q = query(
      articleRef,
      where('source', '==', articleUrl)
    );
    
    const snapshot = await getDocs(q);
    const exists = snapshot.size > 0;
    
    if (exists) {
      console.log('⚠️ Article already saved:', articleUrl);
    }
    
    return exists;
  } catch (error) {
    console.error('Error checking article duplicate:', error);
    return false;
  }
}

/**
 * ✅ Check if podcast already saved by URL or ID
 */
async isPodcastAlreadySaved(podcastUrl) {
  if (!this.currentUser) return false;

  try {
    const podcastRef = collection(db, 'users', this.currentUser.uid, 'savedPodcasts');
    const q = query(
      podcastRef,
      where('audioUrl', '==', podcastUrl)
    );
    
    const snapshot = await getDocs(q);
    const exists = snapshot.size > 0;
    
    if (exists) {
      console.log('⚠️ Podcast already saved:', podcastUrl);
    }
    
    return exists;
  } catch (error) {
    console.error('Error checking podcast duplicate:', error);
    return false;
  }
}

/**
 * ✅ Check if daily brief already saved by date
 */
async isDailyBriefAlreadySaved(generatedFor) {
  if (!this.currentUser) return false;

  try {
    const briefRef = collection(db, 'users', this.currentUser.uid, 'dailyBriefCatchUp');
    const q = query(
      briefRef,
      where('generatedFor', '==', generatedFor)
    );
    
    const snapshot = await getDocs(q);
    const exists = snapshot.size > 0;
    
    if (exists) {
      console.log('⚠️ Daily brief already saved for:', generatedFor);
    }
    
    return exists;
  } catch (error) {
    console.error('Error checking brief duplicate:', error);
    return false;
  }
}

/**
 * ✅ IMPROVED: Save article WITH duplicate check
 */
async saveArticle(articleData) {
  if (!this.currentUser || !this.preptuneUserId) {
    throw new Error('User not authenticated');
  }

  try {
    // ✅ Check if already saved
    const isDuplicate = await this.isArticleAlreadySaved(articleData.source || articleData.url);
    
    if (isDuplicate) {
      console.log('⏭️ Skipping duplicate article:', articleData.title);
      return {
        id: 'duplicate',
        success: false,
        message: 'Article already saved',
        ...articleData
      };
    }

    const articleRef = collection(
      db, 
      'users',
      this.currentUser.uid,
      'savedArticles'
    );

    const savedAt = articleData.savedAt ? new Date(articleData.savedAt).toISOString() : new Date().toISOString();

    const docData = {
      preptuneUserId: this.preptuneUserId,
      userId: this.currentUser.uid,
      
      title: articleData.title || '',
      source: articleData.source || articleData.url || '',
      image: articleData.image || '',
      description: articleData.description || '',
      time: articleData.time || new Date().toLocaleTimeString(),
      
      sourceName: articleData.sourceName || 'Unknown',
      sourceLogo: articleData.sourceLogo || '',
      sourceColor: articleData.sourceColor || '#000000',
      
      savedAt: savedAt,
      createdAt: serverTimestamp(),
      url: articleData.url || articleData.source
    };

    const docRef = await addDoc(articleRef, docData);
    
    console.log('✅ Article saved to Firestore:', docRef.id);
    
    return {
      id: docRef.id,
      success: true,
      ...docData,
      savedAt: savedAt
    };
  } catch (error) {
    console.error('❌ Failed to save article:', error);
    throw error;
  }
}
async removeSavedArticle(articleId) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    console.log('🗑️ Deleting article from Firestore:', articleId);
    
    const articleRef = doc(
      db,
      'users',
      this.currentUser.uid,
      'savedArticles',
      articleId
    );

    await deleteDoc(articleRef);
    
    console.log('✅ Article deleted from Firestore:', articleId);
    return true;
  } catch (error) {
    console.error('❌ Failed to delete article:', error);
    throw error;
  }
}

// ✅ FIXED: Get saved articles with proper date parsing
async getSavedArticles(limitCount = 50) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const articleRef = collection(
      db, 
      'users',
      this.currentUser.uid,
      'savedArticles' // ✅ Match collection name
    );
    
    const q = query(
      articleRef, 
      orderBy('savedAt', 'desc'), 
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    
    const articles = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // ✅ Properly handle dates
      let savedAt = data.savedAt;
      if (data.savedAt && typeof data.savedAt === 'object' && data.savedAt.toDate) {
        savedAt = data.savedAt.toDate().toISOString();
      } else if (typeof data.savedAt === 'string') {
        savedAt = data.savedAt;
      } else {
        savedAt = new Date().toISOString();
      }
      
      articles.push({
        id: doc.id,
        ...data,
        savedAt: savedAt
      });
    });

    console.log(`✅ Loaded ${articles.length} saved articles`);
    return articles;
  } catch (error) {
    console.error('❌ Failed to get saved articles:', error);
    return [];
  }
}
/**
 * ✅ IMPROVED: Save podcast WITH duplicate check
 */
async savePodcast(podcastData) {
  if (!this.currentUser || !this.preptuneUserId) {
    throw new Error('User not authenticated');
  }

  try {
    // ✅ Check if already saved
    const isDuplicate = await this.isPodcastAlreadySaved(podcastData.audioUrl);
    
    if (isDuplicate) {
      console.log('⏭️ Skipping duplicate podcast:', podcastData.title);
      return {
        id: 'duplicate',
        success: false,
        message: 'Podcast already saved',
        ...podcastData
      };
    }

    let audioUrl = podcastData.audioUrl;

    // If it's a blob URL, upload to storage
    if (audioUrl && audioUrl.startsWith('blob:')) {
      console.log('📤 Uploading podcast audio...');
      
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      
      const storageMetadata = {
        title: podcastData.title,
        type: podcastData.type || 'custom',
        duration: podcastData.duration || 0,
        preptuneUserId: this.preptuneUserId,
        hosts: JSON.stringify(podcastData.hosts || ['Host 1', 'Host 2']),
        modulesUsed: JSON.stringify(podcastData.modulesUsed || [])
      };
      
      audioUrl = await uploadAudioBlob(blob, this.currentUser.uid, storageMetadata);
      console.log('✅ Audio uploaded');
    }

    const savedAt = new Date().toISOString();

    const podcastRef = collection(
      db, 
      'users',
      this.currentUser.uid,
      'savedPodcasts'
    );

    const docData = {
      ...podcastData,
      audioUrl,
      preptuneUserId: this.preptuneUserId,
      userId: this.currentUser.uid,
      title: podcastData.title || 'Untitled Podcast',
      duration: podcastData.duration || 0,
      hosts: podcastData.hosts || ['Host 1', 'Host 2'],
      guest: podcastData.guest || null,
      type: podcastData.type || 'custom',
      savedAt: savedAt,
      createdAt: serverTimestamp(),
      listened: false,
      playCount: 0
    };

    const docRef = await addDoc(podcastRef, docData);

    console.log('✅ Podcast saved to Firestore:', docRef.id);

    return {
      id: docRef.id,
      success: true,
      ...docData,
      savedAt: savedAt
    };
  } catch (error) {
    console.error('❌ Failed to save podcast:', error);
    throw error;
  }
}

/**
 * ✅ NEW: Save Daily Brief WITH duplicate check
 */
async saveDailyBriefCatchUp(briefData) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    // ✅ Check if already saved for this date
    const isDuplicate = await this.isDailyBriefAlreadySaved(briefData.generatedFor);
    
    if (isDuplicate) {
      console.log('⏭️ Skipping duplicate brief for:', briefData.generatedFor);
      return {
        id: 'duplicate',
        success: false,
        message: 'Daily brief already saved for this date',
        ...briefData
      };
    }

    const briefRef = collection(
      db,
      'users',
      this.currentUser.uid,
      'dailyBriefCatchUp'
    );

    const docData = {
      ...briefData,
      userId: this.currentUser.uid,
      generatedFor: briefData.generatedFor || new Date().toISOString().split('T')[0],
      generatedAt: serverTimestamp(),
      listened: false,
      playCount: 0
    };

    const docRef = await addDoc(briefRef, docData);

    console.log('✅ Daily brief saved:', docRef.id);
    
    return {
      id: docRef.id,
      success: true,
      ...docData
    };
  } catch (error) {
    console.error('❌ Failed to save daily brief:', error);
    throw error;
  }
}

/**
 * ✅ NEW: Get all saved article URLs (for quick duplicate check)
 */
async getSavedArticleUrls() {
  if (!this.currentUser) return [];

  try {
    const articleRef = collection(db, 'users', this.currentUser.uid, 'savedArticles');
    const snapshot = await getDocs(articleRef);
    
    const urls = [];
    snapshot.forEach(doc => {
      const url = doc.data().source || doc.data().url;
      if (url) urls.push(url);
    });
    
    return urls;
  } catch (error) {
    console.error('Error getting saved article URLs:', error);
    return [];
  }
}

/**
 * ✅ NEW: Get all saved podcast URLs (for quick duplicate check)
 */
async getSavedPodcastUrls() {
  if (!this.currentUser) return [];

  try {
    const podcastRef = collection(db, 'users', this.currentUser.uid, 'savedPodcasts');
    const snapshot = await getDocs(podcastRef);
    
    const urls = [];
    snapshot.forEach(doc => {
      const url = doc.data().audioUrl;
      if (url) urls.push(url);
    });
    
    return urls;
  } catch (error) {
    console.error('Error getting saved podcast URLs:', error);
    return [];
  }
}
async getDailyBriefHistory() {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const userId = this.currentUser.uid;
    
    // ✅ TRY NEW PATH FIRST
    const newBriefsRef = collection(db, 'users', userId, 'dailyBriefCatchUp');
    const newSnapshot = await getDocs(query(newBriefsRef, orderBy('generatedAt', 'desc'), limit(30)));
    
    const newBriefs = newSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      generatedAt: doc.data().generatedAt?.toDate?.() || new Date(doc.data().generatedAt)
    }));
    
    console.log(`✅ New path briefs: ${newBriefs.length}`);
    
    // ✅ FALLBACK: Check OLD path if new path is empty
    if (newBriefs.length === 0) {
      console.log('📭 New path empty, checking old path...');
      
      try {
        const oldBriefsRef = collection(db, 'InnerloopPodcast', userId, 'dailyBriefs');
        const oldSnapshot = await getDocs(query(oldBriefsRef, orderBy('generatedAt', 'desc'), limit(30)));
        
        const oldBriefs = oldSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          generatedAt: doc.data().generatedAt?.toDate?.() || new Date(doc.data().generatedAt)
        }));
        
        console.log(`✅ Old path briefs: ${oldBriefs.length}`);
        
        // ✅ Optionally migrate old briefs to new path
        if (oldBriefs.length > 0) {
          console.log('🔄 Migrating briefs from old path to new path...');
          
          for (const brief of oldBriefs) {
            try {
              const newRef = doc(db, 'users', userId, 'dailyBriefCatchUp', brief.id);
              await setDoc(newRef, brief, { merge: true });
            } catch (e) {
              console.warn('Failed to migrate brief:', brief.id);
            }
          }
        }
        
        return oldBriefs;
      } catch (oldError) {
        console.warn('⚠️ Old path also empty:', oldError.message);
        return [];
      }
    }
    
    return newBriefs;
    
  } catch (error) {
    console.error('❌ Failed to load brief history:', error);
    return [];
  }
}
// ✅ Similarly for daily briefs
async deleteDailyBrief(briefId) {
  try {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    const briefRef = doc(
      db,  // ← Use imported db
      'InnerloopPodcast',
      this.currentUser.uid,  // ← Use this.currentUser.uid
      'dailyBriefs',
      briefId
    );

    await deleteDoc(briefRef);
    console.log('✅ Daily brief deleted:', briefId);
    
    return true;
  } catch (error) {
    console.error('❌ Failed to delete daily brief:', error);
    throw error;
  }
}

// Add these methods to your FirestorePersistentStorageService class

/**
 * ✅ Track shown articles (with 7-day auto-cleanup)
 */
async trackShownArticle(sourceId, articleUrl) {
  if (!this.isReady()) {
    console.error('❌ Service not ready');
    return;
  }

  try {
    const shownRef = doc(
      this.db,
      'users',
      this.userId,
      'shownArticles',
      `${sourceId}_${Date.now()}`
    );

    await setDoc(shownRef, {
      sourceId,
      articleUrl,
      shownAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // ✅ Expires in 7 days
    });

    console.log(`✅ Tracked shown article: ${sourceId}`);
  } catch (error) {
    console.error('❌ Failed to track shown article:', error);
  }
}
// ✅ NEW: Check if news for today is already loaded
async isNewsCacheLoadedForToday() {
  if (!this.currentUser) return false;
  
  try {
    const statusRef = doc(db, 'users', this.currentUser.uid, 'newsLoadStatus', 'today');
    const statusSnap = await getDoc(statusRef);
    
    if (statusSnap.exists()) {
      const data = statusSnap.data();
      const cachedDate = data.cachedDate;
      const today = new Date().toISOString().split('T')[0];
      
      // Check if cached data is from TODAY
      if (cachedDate === today) {
        console.log('✅ News already loaded for today');
        return true;
      }
    }
    
    console.log('📭 No cache status for today');
    return false;
  } catch (error) {
    console.error('Error checking cache status:', error);
    return false;
  }
}

// ✅ NEW: Mark news as loaded for today
async markNewsAsLoadedForToday() {
  if (!this.currentUser) return;
  
  try {
    const today = new Date().toISOString().split('T')[0];
    const statusRef = doc(db, 'users', this.currentUser.uid, 'newsLoadStatus', 'today');
    
    await setDoc(statusRef, {
      cachedDate: today,
      loadedAt: serverTimestamp(),
      sourcesLoaded: true,
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    });
    
    console.log('✅ Marked news as loaded for today');
  } catch (error) {
    console.error('Error marking news as loaded:', error);
  }
}

// ✅ NEW: Clear cache status when day changes
async clearNewsLoadStatus() {
  if (!this.currentUser) return;
  
  try {
    const statusRef = doc(db, 'users', this.currentUser.uid, 'newsLoadStatus', 'today');
    await deleteDoc(statusRef);
    console.log('✅ Cleared news load status');
  } catch (error) {
    console.error('Error clearing status:', error);
  }
}
/**
 * Track podcast play
 */
async trackPodcastPlay(podcastId, isDailyBrief = false) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    // ✅ Use correct collection name based on type
    const collection = isDailyBrief ? 'dailyBriefCatchUp' : 'savedPodcasts';
    
    const podcastRef = doc(
      db, 
      'users',              // ✅ Changed from 'InnerloopPodcast'
      this.currentUser.uid, 
      collection,
      podcastId
    );

    await updateDoc(podcastRef, {
      playCount: increment(1),
      lastPlayedAt: serverTimestamp()
    });

    console.log(`✅ ${isDailyBrief ? 'Daily brief' : 'Podcast'} play tracked`);
  } catch (error) {
    console.error('❌ Failed to track podcast play:', error);
    // Don't throw - this is non-critical tracking
  }
}

/**
 * Delete article
 */
async deleteArticle(articleId) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
const articleRef = doc(
  db, 
  'InnerloopPodcast',   // ✅ CORRECT
  this.currentUser.uid, 
  'articles',           // ✅ CORRECT
  articleId
);

    await deleteDoc(articleRef);
    console.log('✅ Article deleted:', articleId);
  } catch (error) {
    console.error('❌ Failed to delete article:', error);
    throw error;
  }
}


/**
 * Save AI study conversation (quiz/flashcard help)
 */
async saveStudyConversation(conversationData) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const conversationRef = collection(
      db,
      'users',
      this.currentUser.uid,
      'studyConversations'
    );

    const docData = {
      userId: this.currentUser.uid,
      preptuneUserId: this.preptuneUserId,
      
      // Context
      type: conversationData.type, // 'quiz' or 'flashcard'
      contentId: conversationData.contentId, // quiz/flashcard ID
      contentTitle: conversationData.contentTitle,
      questionIndex: conversationData.questionIndex || null,
      
      // Conversation
      messages: conversationData.messages, // [{role: 'user'/'assistant', content: '...', timestamp}]
      duration: conversationData.duration || 0, // seconds
      
      // Metadata
      helpful: conversationData.helpful || null, // user rating
      createdAt: serverTimestamp(),
      lastMessageAt: serverTimestamp()
    };

    const docRef = await addDoc(conversationRef, docData);
    console.log('✅ Study conversation saved:', docRef.id);
    
    return {
      id: docRef.id,
      ...docData
    };
  } catch (error) {
    console.error('❌ Failed to save study conversation:', error);
    throw error;
  }
}

/**
 * Get recent study conversations
 */
async getStudyConversations(limitCount = 20) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const conversationRef = collection(
      db,
      'users',
      this.currentUser.uid,
      'studyConversations'
    );
    
    const q = query(
      conversationRef,
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    
    const conversations = [];
    snapshot.forEach((doc) => {
      conversations.push({
        id: doc.id,
        ...doc.data()
      });
    });

    console.log(`✅ Retrieved ${conversations.length} study conversations`);
    return conversations;
  } catch (error) {
    console.error('❌ Failed to get study conversations:', error);
    return [];
  }
}

// In FirestorePersistentStorageService.js - ADD THIS METHOD

async saveOnboardingData(selectedTools, referralSource) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const userRef = doc(db, 'users', this.currentUser.uid);
    
    // Save to main user doc (not subcollection)
    await updateDoc(userRef, {
      'onboarding.hasCompleted': true,
      'onboarding.selectedTools': selectedTools,
      'onboarding.referralSource': referralSource,
      'onboarding.completedAt': serverTimestamp(),
      'settings.hasCompletedOnboarding': true // Also here for backwards compat
    });
    
    console.log('✅ Onboarding saved to Firestore');
    return true;
  } catch (error) {
    console.error('❌ Failed to save onboarding:', error);
    throw error;
  }
}
// ===== USER SETTINGS =====

/**
 * Save user settings
 */
async saveUserSettings(settings) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const userRef = doc(db, 'users', this.currentUser.uid);
    
    await updateDoc(userRef, {
      'settings.selectedHosts': settings.selectedHosts || ['Host 1', 'Host 2'],
      'settings.newsFeeds': settings.newsFeeds || [],
      'settings.newsCategories': settings.newsCategories || {},
      'settings.selectedStocks': settings.selectedStocks || [],
      'settings.userName': settings.userName || null,
      'settings.updatedAt': serverTimestamp()
    });

    console.log('✅ User settings saved to Firestore');
  } catch (error) {
    console.error('❌ Failed to save user settings:', error);
    throw error;
  }
}

/**
 * Get stock station data (last visit, preferences)
 */
async getStockStationData(ticker) {
  if (!this.isReady() || !ticker) return null;
  
  try {
    const docRef = doc(this.db, 'users', this.userId, 'stockStations', ticker);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting stock station data:', error);
    return null;
  }
}

/**
 * Save stock station visit
 */
async saveStockStationVisit(ticker, data) {
  if (!this.isReady() || !ticker) {
    throw new Error('Service not ready or invalid ticker');
  }
  
  try {
    const docRef = doc(this.db, 'users', this.userId, 'stockStations', ticker);
    
    await setDoc(docRef, {
      ...data,
      lastVisit: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }, { merge: true });
    
    return { id: ticker, ...data };
  } catch (error) {
    console.error('Error saving stock station visit:', error);
    throw error;
  }
}
/**
 * Get current user data (for user name)
 */
async getCurrentUserData() {
  if (!this.auth.currentUser) {
    throw new Error('No user logged in');
  }

  const userId = this.auth.currentUser.uid;
  const doc = await this.db.collection('users').doc(userId).get();
  
  if (!doc.exists) {
    throw new Error('User document not found');
  }
  
  return doc.data();
}


/**
 * ✅ NEW: Update Daily Brief config (for enable/disable toggle)
 */
async updateDailyBriefConfig(updates) {
  if (!this.currentUser) {
    throw new Error('No user logged in');
  }

  const userRef = doc(db, 'users', this.currentUser.uid);
  const updateObject = {};

  // ✅ Build nested update paths for each field
  Object.keys(updates).forEach(key => {
    updateObject[`dailyBriefCatchUp.config.${key}`] = updates[key];
  });

  // ✅ Add timestamp for tracking when config was last updated
  updateObject['dailyBriefCatchUp.config.updatedAt'] = serverTimestamp();

  await updateDoc(userRef, updateObject);
  
  console.log('✅ Daily Brief config updated:', updates);
  console.log('📦 Full update object:', updateObject);
}
/**
 * ✅ Update ONLY the enabled status (for enable/disable toggle)
 */
async updateDailyBriefEnabledStatus(enabled) {
  if (!this.currentUser) {
    throw new Error('No user logged in');
  }

  const userRef = doc(db, 'users', this.currentUser.uid);
  
  await updateDoc(userRef, {
    'dailyBriefCatchUp.config.enabled': enabled,
    'dailyBriefCatchUp.config.updatedAt': serverTimestamp()
  });
  
  console.log('✅ Daily Brief enabled status updated:', enabled);
}

/**
 * Save Local News Station session
 */
async saveLocalNewsSession(sessionData) {
  if (!this.currentUser) {
    console.warn('⚠️ No user for local news session save');
    return;
  }

  try {
    const sessionRef = doc(
      db,
      'users',
      this.currentUser.uid,
      'localNewsStations',
      'currentSession'
    );

    await setDoc(sessionRef, {
      ...sessionData,
      updatedAt: serverTimestamp()
    });

    console.log('✅ Local news session saved to Firestore');
  } catch (error) {
    console.error('❌ Failed to save local news session:', error);
  }
}

/**
 * Get Local News Station session
 */
async getLocalNewsSession() {
  if (!this.currentUser) {
    console.warn('⚠️ No user for local news session load');
    return null;
  }

  try {
    const sessionRef = doc(
      db,
      'users',
      this.currentUser.uid,
      'localNewsStations',
      'currentSession'
    );

    const sessionSnap = await getDoc(sessionRef);

    if (sessionSnap.exists()) {
      const data = sessionSnap.data();
      console.log('✅ Local news session loaded from Firestore');
      return data;
    }

    return null;
  } catch (error) {
    console.error('❌ Failed to load local news session:', error);
    return null;
  }
}

/**
 * Clear Local News Station session
 */
async clearLocalNewsSession() {
  if (!this.currentUser) return;

  try {
    const sessionRef = doc(
      db,
      'users',
      this.currentUser.uid,
      'localNewsStations',
      'currentSession'
    );

    await deleteDoc(sessionRef);
    console.log('🗑️ Local news session cleared from Firestore');
  } catch (error) {
    console.error('❌ Failed to clear local news session:', error);
  }
}

// ✅ IMPROVED: Save news cache with timezone-aware date
// ✅ Make sure this exists
async saveNewsCacheWithDate(newsData, sourcesList) {
  if (!this.currentUser) throw new Error('User not authenticated');
  
  const cleanedNewsData = {};
  for (const [key, value] of Object.entries(newsData)) {
    if (value !== undefined && value !== null) {
      cleanedNewsData[key] = value;
    }
  }
  
  try {
    const userTimezone = this.detectUserTimezone();
    const now = new Date();
    const todayString = now.toLocaleDateString('en-US', {
      timeZone: userTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    
    const userRef = doc(db, 'users', this.currentUser.uid);
    
    await updateDoc(userRef, {
      'newsCache.data': cleanedNewsData,
      'newsCache.sources': sourcesList,
      'newsCache.fetchedAt': serverTimestamp(),
      'newsCache.fetchedInTimezone': userTimezone,
      'newsCache.cachedDate': todayString
    });
    
    console.log('✅ News cache saved:', sourcesList.length, 'sources');
    return { success: true };
  } catch (error) {
    console.error('❌ Failed to cache news:', error);
    throw error;
  }
}

// ✅ Make sure this exists
async getCachedNewsDataWithDateCheck() {
  if (!this.currentUser) return null;
  
  try {
    const userRef = doc(db, 'users', this.currentUser.uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) return null;
    
    const cached = userDoc.data()?.newsCache;
    
    if (!cached?.data || !cached?.fetchedAt) {
      console.log('📭 No cached news found');
      return null;
    }
    
    // Check if cache is from TODAY
    const userTimezone = this.detectUserTimezone();
    const now = new Date();
    
    const todayString = now.toLocaleDateString('en-US', {
      timeZone: userTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    
    if (cached.cachedDate !== todayString) {
      console.log(`📅 Cache from ${cached.cachedDate}, today is ${todayString} - expired`);
      return null;
    }
    
    console.log('✅ Cache is valid (today)');
    return {
      data: cached.data,
      sources: cached.sources,
      fetchedAt: cached.fetchedAt
    };
  } catch (error) {
    console.error('❌ Error checking cache:', error);
    return null;
  }
}

// ✅ Make sure this exists
async getGlobalDailyNewsSnapshot() {
  try {
    const docSnap = await getDoc(doc(db, 'global', 'dailyNewsSnapshot'));
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      console.log(`✅ Global snapshot loaded: ${data.sourceCount} sources`);
      return data.newsData;
    }
    
    console.log('📭 Global snapshot not available');
    return null;
  } catch (error) {
    console.error('❌ Failed to load global snapshot:', error);
    return null;
  }
}


// ✅ Clear news cache
async clearNewsCache() {
  if (!this.currentUser) return;
  
  try {
    const userRef = doc(db, 'users', this.currentUser.uid);
    
    await updateDoc(userRef, {
      'newsCache.data': deleteField(),
      'newsCache.sources': deleteField(),
      'newsCache.fetchedAt': deleteField()
    });
    
    console.log('✅ News cache cleared');
  } catch (error) {
    console.error('❌ Failed to clear cache:', error);
  }
}
/**
 * Admin test: Trigger brief generation
 */
async adminTestDailyBriefCatchUp() {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  if (!this.isAdmin()) {
    throw new Error('Only admins can test generation');
  }

  try {
    // Call your backend function here
    // This is a placeholder - replace with your actual backend call
    const response = await fetch(      'https://us-central1-fir-7c9c9.cloudfunctions.net/adminForceGenerateDailyBriefCatchUp', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: this.currentUser.uid
      })
    });

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Admin test failed:', error);
    throw error;
  }
}

/**
 * Check if current user is admin
 */
isAdmin() {
  if (!this.auth.currentUser) {
    return false;
  }
  
  const adminEmails = [
    'kogolof1@montclair.edu',
    'franklyn@preptune.com',
    'support@preptune.com'
  ];
  
  return adminEmails.includes(this.auth.currentUser.email?.toLowerCase());
}

/**
 * Get user settings
 */
async getUserSettings() {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const userRef = doc(db, 'users', this.currentUser.uid);
    const docSnap = await getDoc(userRef);
    
    if (docSnap.exists()) {
      const settings = docSnap.data().settings || {};
      
      console.log('📖 Loaded settings:', {
        hasCompletedOnboarding: settings.hasCompletedOnboarding,
        selectedTools: settings.selectedTools,
        referralSource: settings.referralSource
      });
      
      return settings;
    }
    
    return {};
  } catch (error) {
    console.error('❌ Failed to get user settings:', error);
    return {};
  }
}
getNewsService() {
  if (!this.newsService && this.currentUser) {
    const NewsManagementService = require('./NewsManagementService').default;
    this.newsService = new NewsManagementService(db, this.currentUser.uid);
  }
  return this.newsService;
}


// ✅ NEW: Save selected news sources
async saveSelectedNewsSources(selectedSources) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }
  
  console.log('💾 Saving selected news sources:', selectedSources);
  
  try {
    const userRef = doc(db, 'users', this.currentUser.uid);
    
    await updateDoc(userRef, {
      'newsSettings.selectedSources': selectedSources,
      'newsSettings.lastUpdated': serverTimestamp()
    });
    
    console.log('✅ News sources saved to Firestore');
    return { success: true, sources: selectedSources };
  } catch (error) {
    console.error('❌ Failed to save news sources:', error);
    throw error;
  }
}

// ✅ NEW: Get selected news sources
async getSelectedNewsSources() {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }
  
  try {
    const userRef = doc(db, 'users', this.currentUser.uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const sources = userDoc.data()?.newsSettings?.selectedSources || [];
      console.log('✅ Loaded selected news sources:', sources);
      return {
        sources: sources.length > 0 ? sources : ['abc', 'variety', 'yahoo-sports'],
        lastUpdated: userDoc.data()?.newsSettings?.lastUpdated
      };
    }
    
    return { sources: ['abc', 'variety', 'yahoo-sports'], lastUpdated: null };
  } catch (error) {
    console.error('❌ Failed to get news sources:', error);
    return { sources: ['abc', 'variety', 'yahoo-sports'], lastUpdated: null };
  }
}

/**
 * ✅ NEW: Cache fetched news data
 * IMPORTANT: Validates newsData before saving
 */
async cacheNewsData(newsData, sourcesList) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }
  
  // ✅ CRITICAL FIX: Validate newsData before saving
  if (!newsData || typeof newsData !== 'object') {
    console.error('❌ Invalid newsData:', newsData);
    throw new Error('Invalid news data: must be an object');
  }
  
  // Remove any undefined values from newsData
  const cleanedNewsData = {};
  for (const [key, value] of Object.entries(newsData)) {
    if (value !== undefined) {
      cleanedNewsData[key] = value;
    }
  }
  
  if (Object.keys(cleanedNewsData).length === 0) {
    console.warn('⚠️ No valid news data to cache');
    return { success: false, message: 'No valid data' };
  }
  
  console.log('💾 Caching news data:', {
    sources: sourcesList,
    dataKeys: Object.keys(cleanedNewsData)
  });
  
  try {
    const newsService = this.getNewsService();
    return await newsService.saveNewsCache(cleanedNewsData, sourcesList);
  } catch (error) {
    console.error('❌ Failed to cache news data:', error);
    throw error;
  }
}

/**
 * ✅ NEW: Get cached news data
 */
async getCachedNewsData() {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }
  
  try {
    const newsService = this.getNewsService();
    return await newsService.getNewsCache();
  } catch (error) {
    console.error('❌ Failed to get cached news:', error);
    return null;
  }
}

/**
 * ✅ NEW: Check if cache is valid for sources
 */
async isCacheValidForSources(requestedSources) {
  if (!this.currentUser) return false;
  
  try {
    const newsService = this.getNewsService();
    return await newsService.isCacheValidForSources(requestedSources);
  } catch (error) {
    console.error('❌ Failed to validate cache:', error);
    return false;
  }
}

/**
 * ✅ NEW: Clear news cache
 */
async clearNewsCache() {
  if (!this.currentUser) return;
  
  try {
    const newsService = this.getNewsService();
    return await newsService.clearCache();
  } catch (error) {
    console.error('❌ Failed to clear cache:', error);
  }
}


async savePodcast(podcastData) {
  if (!this.currentUser || !this.preptuneUserId) {
    throw new Error('User not authenticated');
  }

  try {
    let audioUrl = podcastData.audioUrl;

    // If it's a blob URL, upload to storage
    if (audioUrl && audioUrl.startsWith('blob:')) {
      console.log('📤 Uploading podcast audio...');
      
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      
      const storageMetadata = {
        title: podcastData.title,
        type: podcastData.type || 'custom',
        duration: podcastData.duration || 0,
        preptuneUserId: this.preptuneUserId,
        hosts: JSON.stringify(podcastData.hosts || ['Host 1', 'Host 2']),
        modulesUsed: JSON.stringify(podcastData.modulesUsed || [])
      };
      
      audioUrl = await uploadAudioBlob(blob, this.currentUser.uid, storageMetadata);
      console.log('✅ Audio uploaded');
    }

    // ✅ Ensure proper date format
    const savedAt = new Date().toISOString();

    const podcastRef = collection(
      db, 
      'users',
      this.currentUser.uid,
      'savedPodcasts'
    );

    const docData = {
      ...podcastData,
      audioUrl,
      preptuneUserId: this.preptuneUserId,
      userId: this.currentUser.uid,
      title: podcastData.title || 'Untitled Podcast',
      duration: podcastData.duration || 0,
      hosts: podcastData.hosts || ['Host 1', 'Host 2'],
      guest: podcastData.guest || null,
      type: podcastData.type || 'custom',
      savedAt: savedAt, // ✅ ISO string format
      createdAt: serverTimestamp(),
      listened: false,
      playCount: 0
    };

    const docRef = await addDoc(podcastRef, docData);

    console.log('✅ Podcast saved to Firestore:', docRef.id);

    return {
      id: docRef.id,
      ...docData,
      savedAt: savedAt
    };
  } catch (error) {
    console.error('❌ Failed to save podcast:', error);
    throw error;
  }
}


/**
 * ✅ ENHANCED: Mark brief as listened (resets auto-disable counter)
 */
async markDailyBriefAsListened(briefId) {
  if (!this.currentUser) {
    throw new Error('No user logged in');
  }

  try {
    // Mark the specific brief as listened
    const briefRef = doc(
      db,
      'users',
      this.currentUser.uid,
      'dailyBriefCatchUp',
      briefId
    );

    await updateDoc(briefRef, {
      listened: true,
      listenedAt: serverTimestamp(),
      playCount: increment(1)
    });

    // ✅ Reset the consecutive unlistened counter (prevents auto-disable)
    const userRef = doc(db, 'users', this.currentUser.uid);
    await updateDoc(userRef, {
      'dailyBriefCatchUp.config.consecutiveUnlistenedDays': 0,
      'dailyBriefCatchUp.config.lastListenedDate': new Date().toISOString()
    });

    console.log('✅ Brief marked as listened, counter reset');
  } catch (error) {
    console.error('Failed to mark brief as listened:', error);
    throw error;
  }
}

// ✅ FIXED: Get saved podcasts with proper date parsing
async getSavedPodcasts(limitCount = 20) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const podcastRef = collection(
      db, 
      'users',
      this.currentUser.uid, 
      'savedPodcasts'
    );
    
    const q = query(
      podcastRef, 
      orderBy('savedAt', 'desc'), 
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    
    const podcasts = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // ✅ Properly handle dates
      let savedAt = data.savedAt;
      if (data.savedAt && typeof data.savedAt === 'object' && data.savedAt.toDate) {
        savedAt = data.savedAt.toDate().toISOString();
      } else if (typeof data.savedAt === 'string') {
        savedAt = data.savedAt;
      } else {
        savedAt = new Date().toISOString();
      }
      
      podcasts.push({
        id: doc.id,
        ...data,
        savedAt: savedAt,
        // Ensure these fields exist
        listened: data.listened || false,
        playCount: data.playCount || 0,
        type: data.type || 'custom'
      });
    });

    console.log(`✅ Loaded ${podcasts.length} saved podcasts`);
    return podcasts;
  } catch (error) {
    console.error('❌ Failed to get saved podcasts:', error);
    return [];
  }
}
async savePodcastPreferences(preferences) {
  if (!this.currentUser) {
    console.error('❌ User not authenticated');
    return false;
  }

  try {
    const userRef = doc(db, 'users', this.currentUser.uid);
    
    const preferencesToSave = {
      hosts: Array.isArray(preferences.hosts) ? preferences.hosts : ['Host 1', 'Host 2'],
      guest: preferences.guest || null,
      talkingStyle: preferences.talkingStyle || 'casual',
      introMusicEnabled: preferences.introMusicEnabled !== undefined ? preferences.introMusicEnabled : true,
      lastUpdated: serverTimestamp(),
      syncedAt: new Date().toISOString(),
      userId: this.currentUser.uid // ✅ Track user
    };

    // ✅ Save to Firestore
    await updateDoc(userRef, {
      'podcastPreferences': preferencesToSave
    });

    // ✅ ALSO save to PER-USER localStorage
    const storageKey = this.getPerUserStorageKey('podcast_preferences');
    localStorage.setItem(storageKey, JSON.stringify(preferencesToSave));

    return true;
  } catch (error) {
    console.error('❌ Failed to save preferences:', error);
    
    // ✅ Fallback to PER-USER localStorage
    try {
      const preferencesToSave = {
        hosts: Array.isArray(preferences.hosts) ? preferences.hosts : ['Host 1', 'Host 2'],
        guest: preferences.guest || null,
        talkingStyle: preferences.talkingStyle || 'casual',
        introMusicEnabled: preferences.introMusicEnabled !== undefined ? preferences.introMusicEnabled : true,
        localOnly: true,
        savedAt: new Date().toISOString(),
        userId: this.currentUser?.uid
      };
      
      const storageKey = this.getPerUserStorageKey('podcast_preferences');
      localStorage.setItem(storageKey, JSON.stringify(preferencesToSave));
      return true;
    } catch (localError) {
      return false;
    }
  }
}


// ============================================
// QUIZ METHODS
// ============================================

/**
 * Save a custom quiz to Firestore
 */
async saveCustomQuiz(quizData) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const quizRef = collection(db, 'users', this.currentUser.uid, 'generatedQuizzes');

    // ✅ Don't save 'type' or 'quiz' fields - only 'questions'
    const quizDocument = {
      id: quizData.id,
      title: quizData.title || 'Untitled Quiz',
      description: quizData.description || '',
      difficulty: quizData.difficulty || 'medium',
      topic: quizData.topic || '',
      questions: quizData.questions || [],  // ✅ Only save 'questions' array
      questionCount: (quizData.questions || []).length,
      isCustom: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      averageScore: 0,
      completionCount: 0
      // ✅ DO NOT include 'type' or 'quiz' fields
    };

    await setDoc(doc(quizRef, quizData.id), quizDocument);
    
    console.log('✅ Quiz saved to Firestore:', quizData.id);
    
    return {
      success: true,
      quizId: quizData.id,
      quiz: quizDocument
    };
  } catch (error) {
    console.error('❌ Failed to save quiz:', error);
    throw error;
  }
}
/**
 * Get quiz history for the current user
 */
async getQuizHistory(limitCount = 50) {
  if (!this.currentUser) {
    console.error('❌ User not authenticated');
    return [];
  }


  try {
    const historyRef = collection(db, 'users', this.currentUser.uid, 'quizHistory');
    const q = query(historyRef, orderBy('completedAt', 'desc'));
    const snapshot = await getDocs(q);
    
    const history = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      completedAt: doc.data().completedAt?.toDate() || new Date()
    }));
    
    console.log(`✅ Loaded ${history.length} quiz attempts`);
    return history;
  } catch (error) {
    console.error('❌ Failed to get quiz history:', error);
    return [];
  }
}

/**
 * Get all generated quizzes for the current user
 */
async getGeneratedQuizzes() {
  if (!this.currentUser) {
    console.error('❌ User not authenticated');
    return [];
  }

  try {
    const quizzesRef = collection(db, 'users', this.currentUser.uid, 'generatedQuizzes');
    const q = query(quizzesRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    const quizzes = snapshot.docs.map(doc => {
      const data = doc.data();
      
      // ✅ Clean up the data - remove conflicting fields
      const { type, quiz, ...cleanData } = data;
      
      return {
        id: doc.id,
        ...cleanData,
        questions: data.questions || data.quiz || [],  // ✅ Fallback for old data
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date()
      };
    });
    
    console.log(`✅ Loaded ${quizzes.length} generated quizzes`);
    return quizzes;
  } catch (error) {
    console.error('❌ Failed to get generated quizzes:', error);
    return [];
  }
}
async getGeneratedFlashcards() {
  if (!this.currentUser) {
    console.error('❌ User not authenticated');
    return [];
  }

  try {
    const setsRef = collection(db, 'users', this.currentUser.uid, 'generatedFlashcards');
    const q = query(setsRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    const sets = snapshot.docs.map(doc => {
      const data = doc.data();
      
      // ✅ Clean up the data - remove conflicting fields
      const { type, cards, ...cleanData } = data;
      
      return {
        id: doc.id,
        ...cleanData,
        flashcards: data.flashcards || data.cards || [],  // ✅ Fallback for old data
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date()
      };
    });
    
    console.log(`✅ Loaded ${sets.length} generated flashcard sets`);
    return sets;
  } catch (error) {
    console.error('❌ Failed to get generated flashcards:', error);
    return [];
  }
}

// ============================================
// FLASHCARD METHODS
// ============================================

/**
 * Save custom flashcard set to Firestore
 */
async saveCustomFlashcards(flashcardData) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const setRef = collection(db, 'users', this.currentUser.uid, 'generatedFlashcards');
    
    // ✅ Don't save 'type' or 'cards' fields - only 'flashcards'
    const flashcardDocument = {
      id: flashcardData.id,
      title: flashcardData.title || 'Untitled Flashcard Set',
      description: flashcardData.description || '',
      difficulty: flashcardData.difficulty || 'medium',
      topic: flashcardData.topic || '',
      flashcards: flashcardData.flashcards || [],  // ✅ Only save 'flashcards' array
      cardCount: (flashcardData.flashcards || []).length,
      isCustom: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      studyCount: 0,
      masteredCount: 0
      // ✅ DO NOT include 'type' or 'cards' fields
    };

    await setDoc(doc(setRef, flashcardData.id), flashcardDocument);
    
    console.log('✅ Flashcard set saved to Firestore:', flashcardData.id);
    
    return {
      success: true,
      setId: flashcardData.id,
      flashcardSet: flashcardDocument
    };
  } catch (error) {
    console.error('❌ Failed to save flashcard set:', error);
    throw error;
  }
}

/**
 * Delete a quiz
 */
async deleteQuiz(quizId) {
  if (!this.currentUser) {
    console.error('❌ User not authenticated');
    return false;
  }

  try {
    const quizRef = doc(db, 'users', this.currentUser.uid, 'generatedQuizzes', quizId);
    await deleteDoc(quizRef);
    console.log('✅ Quiz deleted:', quizId);
    return true;
  } catch (error) {
    console.error('❌ Failed to delete quiz:', error);
    return false;
  }
}
/**
 * Delete a flashcard set
 */
async deleteFlashcardSet(setId) {
  if (!this.currentUser) {
    console.error('❌ User not authenticated');
    return false;
  }

  try {
    const setRef = doc(db, 'users', this.currentUser.uid, 'generatedFlashcards', setId);
    await deleteDoc(setRef);
    console.log('✅ Flashcard set deleted:', setId);
    return true;
  } catch (error) {
    console.error('❌ Failed to delete flashcard set:', error);
    return false;
  }
}
/**
 * ✅ Generate per-user localStorage key
 * Each user gets their own storage namespace
 */
getPerUserStorageKey(preferenceType = 'podcast_preferences') {
  if (!this.currentUser?.uid) {
    return `preptune_${preferenceType}`;
  }
  return `preptune_${this.currentUser.uid}_${preferenceType}`;
}
async getPodcastPreferences() {
  if (!this.currentUser) {
    return {
      hosts: ['Host 1', 'Host 2'],
      guest: null,
      talkingStyle: 'casual',
      introMusicEnabled: true,
      source: 'default_no_user'
    };
  }

  try {
    // ✅ STEP 1: Try Firestore
    const userRef = doc(db, 'users', this.currentUser.uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const prefs = userData.podcastPreferences;
      
      if (prefs && Object.keys(prefs).length > 0) {
        const validPrefs = {
          hosts: Array.isArray(prefs.hosts) && prefs.hosts.length > 0 ? prefs.hosts : ['Host 1', 'Host 2'],
          guest: prefs.guest || null,
          talkingStyle: prefs.talkingStyle || 'casual',
          introMusicEnabled: prefs.introMusicEnabled !== undefined ? prefs.introMusicEnabled : true,
          source: 'firestore'
        };
        
        // ✅ Sync to PER-USER localStorage
        const storageKey = this.getPerUserStorageKey('podcast_preferences');
        localStorage.setItem(storageKey, JSON.stringify(validPrefs));
        
        return validPrefs;
      }
    }
    
    // ✅ STEP 2: Fallback to PER-USER localStorage
    const storageKey = this.getPerUserStorageKey('podcast_preferences');
    const cached = localStorage.getItem(storageKey);
    
    if (cached) {
      try {
        const prefs = JSON.parse(cached);
        console.log(`✅ Preferences loaded from localStorage`);
        return {
          ...prefs,
          source: 'localStorage'
        };
      } catch (parseError) {
        localStorage.removeItem(storageKey);
      }
    }
    
    // ✅ STEP 3: Return defaults
    return {
      hosts: ['Host 1', 'Host 2'],
      guest: null,
      talkingStyle: 'casual',
      introMusicEnabled: true,
      source: 'default'
    };
    
  } catch (error) {
    console.error('❌ Error loading preferences:', error);
    
    // ✅ Last resort: PER-USER localStorage
    try {
      const storageKey = this.getPerUserStorageKey('podcast_preferences');
      const cached = localStorage.getItem(storageKey);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (e) {}
    
    return {
      hosts: ['Host 1', 'Host 2'],
      guest: null,
      talkingStyle: 'casual',
      introMusicEnabled: true,
      source: 'error_fallback'
    };
  }
}


// ✅ Smart timezone detection
detectUserTimezone() {
  try {
    // Use browser's Intl API - most reliable
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    console.log('🌍 Auto-detected timezone:', timezone);
    // Examples: "America/New_York", "America/Los_Angeles", "Europe/London"
    
    return timezone;
  } catch (error) {
    console.error('Failed to detect timezone:', error);
    // Fallback to Eastern Time
    return 'America/New_York';
  }
}

// ✅ Update timezone if user changes location
async updateTimezoneIfChanged() {
  if (!this.currentUser) return;
  
  try {
    const currentDetectedTZ = this.detectUserTimezone();
    const userData = await this.getUserData();
    const storedTZ = userData.timezone;
    
    // Only update if changed
    if (currentDetectedTZ !== storedTZ) {
      console.log(`🌍 Timezone changed: ${storedTZ} → ${currentDetectedTZ}`);
      
      await this.updateUserData({
        timezone: currentDetectedTZ,
        timezoneUpdatedAt: serverTimestamp(),
        previousTimezone: storedTZ
      });
      
      console.log('✅ Timezone auto-updated');
    }
  } catch (error) {
    console.error('Failed to update timezone:', error);
  }
}

// ✅ ADD THIS METHOD: Update user's timezone
async updateUserTimezone(timezone) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const userRef = doc(db, 'users', this.currentUser.uid);
    
    await updateDoc(userRef, {
      timezone: timezone,
      timezoneUpdatedAt: serverTimestamp(),
      timezoneDetectionMethod: 'auto_detected'
    });

    console.log('✅ User timezone updated to:', timezone);
    return { success: true, timezone };
  } catch (error) {
    console.error('❌ Failed to update user timezone:', error);
    throw error;
  }
}
// Add to FirestorePersistentStorageService.js

async migrateExistingUsersTimezone() {
  if (!this.isAdmin()) {
    throw new Error('Admin access required');
  }

  try {
    console.log('🔄 Starting timezone migration...');
    
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    let migrated = 0;
    let alreadySet = 0;
    
    const batch = writeBatch(db);
    
    snapshot.forEach((docSnap) => {
      const userData = docSnap.data();
      
      // Skip if already has timezone
      if (userData.timezone) {
        alreadySet++;
        return;
      }
      
      // Default to Eastern Time (safest assumption for US users)
      batch.update(docSnap.ref, {
        timezone: 'America/New_York',
        timezoneMigrated: serverTimestamp(),
        timezoneDetectionMethod: 'migration_default'
      });
      
      migrated++;
    });
    
    await batch.commit();
    
    console.log(`✅ Migration complete:`);
    console.log(`   Migrated: ${migrated} users`);
    console.log(`   Already set: ${alreadySet} users`);
    
    return { migrated, alreadySet };
    
  } catch (error) {
    console.error('❌ Migration failed:', error);
    throw error;
  }
}

// ✅ Get timezone info for display
getTimezoneInfo() {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Get current time in that timezone
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZoneName: 'short'
    });
    
    const currentTime = formatter.format(now);
    
    return {
      timezone: timezone,
      currentTime: currentTime,
      offset: this.getTimezoneOffset(timezone)
    };
    
  } catch (error) {
    return null;
  }
}

getTimezoneOffset(timezone) {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'longOffset'
    });
    
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find(part => part.type === 'timeZoneName');
    
    return offsetPart ? offsetPart.value : '';
  } catch (error) {
    return '';
  }
}
// ✅ Call this once from admin panel
async adminMigrateTimezones() {
  if (!this.isAdmin()) {
    throw new Error('Admin access required');
  }
  
  await this.migrateExistingUsersTimezone();
  
  await this.logAdminAction({
    action: 'timezone_migration',
    adminUserId: this.currentUser.uid,
    performedBy: this.currentUser.email,
    timestamp: new Date().toISOString()
  });
}
/**
 * ✅ Clear ALL seen articles (reset everything)
 */
async clearAllSeenArticles() {
  try {
    if (!this.userId) {
      console.warn('No userId, cannot clear seen articles');
      return;
    }

    const seenArticlesRef = collection(this.db, 'users', this.userId, 'seenArticles');
    const snapshot = await getDocs(seenArticlesRef);

    const batch = writeBatch(this.db);
    snapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    await batch.commit();
    console.log('✅ All seen articles cleared');
  } catch (error) {
    console.error('Error clearing all seen articles:', error);
  }
}

/**
 * Get storage metadata for a specific audio file
 */
async getAudioMetadata(audioUrl) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    // Extract path from URL
    const path = audioUrl.split('/o/')[1]?.split('?')[0];
    if (!path) {
      throw new Error('Invalid audio URL');
    }
    
    const decodedPath = decodeURIComponent(path);
    const audioRef = ref(storage, decodedPath);
    
    const metadata = await getMetadata(audioRef);
    
    console.log('📋 Retrieved audio metadata:', metadata.customMetadata);
    return metadata.customMetadata;
  } catch (error) {
    console.error('❌ Failed to get audio metadata:', error);
    return null;
  }
}

/**
 * Update storage metadata for an existing file
 */
async updateAudioMetadata(audioUrl, newMetadata) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const path = audioUrl.split('/o/')[1]?.split('?')[0];
    if (!path) {
      throw new Error('Invalid audio URL');
    }
    
    const decodedPath = decodeURIComponent(path);
    const audioRef = ref(storage, decodedPath);
    
    const updatedMetadata = {
      customMetadata: {
        ...newMetadata,
        lastUpdated: new Date().toISOString()
      }
    };
    
    await updateMetadata(audioRef, updatedMetadata);
    
    console.log('✅ Audio metadata updated');
    return true;
  } catch (error) {
    console.error('❌ Failed to update audio metadata:', error);
    throw error;
  }
}
/**
 * Get all podcast files with metadata for current user
 */
async listUserPodcastsFromStorage() {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const podcastsRef = ref(storage, `podcasts/${this.currentUser.uid}`);
    const listResult = await listAll(podcastsRef);
    
    const podcastsWithMetadata = await Promise.all(
      listResult.items.map(async (itemRef) => {
        try {
          const metadata = await getMetadata(itemRef);
          const url = await getDownloadURL(itemRef);
          
          return {
            name: itemRef.name,
            url,
            size: metadata.size,
            contentType: metadata.contentType,
            createdAt: metadata.timeCreated,
            updatedAt: metadata.updated,
            customMetadata: metadata.customMetadata || {}
          };
        } catch (error) {
          console.error('Failed to get metadata for:', itemRef.name);
          return null;
        }
      })
    );
    
    return podcastsWithMetadata.filter(Boolean);
  } catch (error) {
    console.error('❌ Failed to list podcasts from storage:', error);
    return [];
  }
}

/**
 * Admin: Get storage usage statistics
 */
async getStorageStats() {
  if (!this.isAdmin()) {
    throw new Error('Admin access required');
  }

  try {
    const podcastsRef = ref(storage, 'podcasts');
    const listResult = await listAll(podcastsRef);
    
    let totalSize = 0;
    let totalFiles = 0;
    const userStats = {};
    
    for (const prefix of listResult.prefixes) {
      const userId = prefix.name;
      const userRef = ref(storage, `podcasts/${userId}`);
      const userList = await listAll(userRef);
      
      let userSize = 0;
      for (const item of userList.items) {
        const metadata = await getMetadata(item);
        userSize += metadata.size;
        totalSize += metadata.size;
        totalFiles++;
      }
      
      userStats[userId] = {
        files: userList.items.length,
        totalSize: userSize,
        sizeMB: (userSize / (1024 * 1024)).toFixed(2)
      };
    }
    
    return {
      totalFiles,
      totalSize,
      totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2),
      totalSizeGB: (totalSize / (1024 * 1024 * 1024)).toFixed(2),
      userStats
    };
  } catch (error) {
    console.error('❌ Failed to get storage stats:', error);
    throw error;
  }
}

// ===== TRAFFIC ROUTES METHODS =====

/**
 * Save traffic route
 */
// ✅ CORRECT - Already using proper path
async saveTrafficRoute(routeData) {
  if (!this.currentUser || !this.preptuneUserId) {
    throw new Error('User not authenticated');
  }

  try {
    const routesRef = collection(
      db,
      'users',                    // ✅ CORRECT
      this.currentUser.uid, 
      'trafficRoutes'             // ✅ CORRECT subcollection
    );

    const docData = {
      preptuneUserId: this.preptuneUserId,
      userId: this.currentUser.uid,
      
      name: routeData.name,
      origin: routeData.origin,
      destination: routeData.destination,
      
      // ✅ CRITICAL: Store coordinates as separate fields
      originLat: routeData.originLat || routeData.originCoords?.lat,
      originLon: routeData.originLon || routeData.originCoords?.lon,
      destLat: routeData.destLat || routeData.destCoords?.lat,
      destLon: routeData.destLon || routeData.destCoords?.lon,
      
      // ✅ Also keep string format for backwards compatibility
      originCoords: routeData.originCoords,
      destCoords: routeData.destCoords,
      
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(routesRef, docData);
    
    console.log('✅ Traffic route saved:', docRef.id);
    
    return {
      id: docRef.id,
      ...docData
    };
  } catch (error) {
    console.error('❌ Failed to save route:', error);
    throw error;
  }
}

// ✅ CORRECT - Get from proper path
async getTrafficRoutes() {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const routesRef = collection(
      db,
      'users',                    // ✅ CORRECT
      this.currentUser.uid,
      'trafficRoutes'
    );
    
    const q = query(routesRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const routes = [];
    querySnapshot.forEach((doc) => {
      routes.push({
        id: doc.id,
        ...doc.data()
      });
    });

    console.log(`✅ Retrieved ${routes.length} traffic routes`);
    return routes;
  } catch (error) {
    console.error('❌ Failed to get routes:', error);
    return [];
  }
}

async updateTrafficRoute(routeId, routeData) {
  if (!this.currentUser) {  // ✅ CORRECT
    throw new Error('User not authenticated');
  }

  try {
    const routeRef = doc(
      db,
      'users',
      this.currentUser.uid,
      'trafficRoutes',
      routeId
    );


    await updateDoc(routeRef, {
      ...routeData,
      updatedAt: serverTimestamp()
    });

    console.log('✅ Traffic route updated:', routeId);
    
    return {
      id: routeId,
      ...routeData,
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('❌ Failed to update route:', error);
    throw error;
  }
}
/**
 * Delete traffic route
 */
async deleteTrafficRoute(routeId) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const routeRef = doc(
      db,
      'users',
      this.currentUser.uid,
      'trafficRoutes',
      routeId
    );

    await deleteDoc(routeRef);
    console.log('✅ Route deleted:', routeId);
  } catch (error) {
    console.error('❌ Failed to delete route:', error);
    throw error;
  }
}

// ===== ADMIN TESTING FEATURES =====

/**
 * Admin: Force generate daily brief immediately (for testing)
 */
async adminForceGenerateDailyBrief() {
  if (!this.isAdmin()) {
    throw new Error('Admin access required');
  }

  try {
    console.log('🧪 Setting up admin test generation...');
    
    const userDocRef = doc(db, 'users', this.currentUser.uid);

    // Set flag for backend to generate in 30 seconds
    const nextGen = new Date();
    nextGen.setSeconds(nextGen.getSeconds() + 30);

    await updateDoc(userDocRef, {
      'dailyBrief.adminTestMode': true,
      'dailyBrief.nextGenerationTime': nextGen.toISOString(),
      'dailyBrief.testModeSetAt': serverTimestamp(),
      'dailyBrief.testModeSetBy': this.currentUser.email
    });

    console.log('✅ Admin test mode activated - brief will generate in 30 seconds');
    
    return {
      success: true,
      nextGenerationTime: nextGen.toISOString(),
      message: 'Daily brief will be generated in 30 seconds'
    };
  } catch (error) {
    console.error('❌ Failed to set admin test mode:', error);
    throw error;
  }
}

/**
 * Admin: Reset daily brief test mode
 */
async adminResetDailyBriefTestMode() {
  if (!this.isAdmin()) {
    throw new Error('Admin access required');
  }

  try {
    const userDocRef = doc(db, 'users', this.currentUser.uid);

    await updateDoc(userDocRef, {
      'dailyBrief.adminTestMode': false,
      'dailyBrief.nextGenerationTime': null,
      'dailyBrief.testModeResetAt': serverTimestamp()
    });

    console.log('✅ Admin test mode reset');
    return { success: true };
  } catch (error) {
    console.error('❌ Failed to reset test mode:', error);
    throw error;
  }
}

// ============================================
// DAILY BRIEF CATCHUP METHODS (NEW SYSTEM)
// Collection: DailyBriefCatchUp
// ============================================

/**
 * Save Daily Brief CatchUp configuration
 */
async saveDailyBriefCatchUpConfig(config) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const userDocRef = doc(db, 'users', this.currentUser.uid);

    const configData = {
      enabled: true,
      time: config.time, // Format: "07:00"
      modules: config.modules || [],
      trafficRoute: config.trafficRoute || null,
      hosts: config.hosts || ['Host 1', 'Host 2'],
      guest: config.guest || null,
      timezone: config.timezone || this.detectUserTimezone(),
      setupAt: serverTimestamp(),
      lastUpdated: serverTimestamp()
    };

    await updateDoc(userDocRef, {
      'dailyBriefCatchUp.config': configData
    });

    console.log('✅ Daily Brief CatchUp config saved:', configData);
    return { success: true, config: configData };
  } catch (error) {
    console.error('❌ Failed to save Daily Brief CatchUp config:', error);
    throw error;
  }
}


/**
 * ✅ Get Daily Brief config (returns null if disabled OR not set up)
 * This differentiates between "never set up" vs "disabled"
 */
async getDailyBriefCatchUpConfig() {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const userDocRef = doc(db, 'users', this.currentUser.uid);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      const config = userDoc.data().dailyBriefCatchUp?.config;
      
      // ✅ Return config even if disabled (so we know it was set up)
      // Frontend can check config.enabled to show appropriate UI
      return config || null;
    }
    
    return null;
  } catch (error) {
    console.error('❌ Failed to get Daily Brief CatchUp config:', error);
    return null;
  }
}
/**
 * ✅ Update just the enabled/disabled status + clear auto-disable flags
 */
async updateDailyBriefEnabledStatus(enabled) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const userRef = doc(db, 'users', this.currentUser.uid);
    
    const updates = {
      'dailyBriefCatchUp.config.enabled': enabled,
      'dailyBriefCatchUp.config.consecutiveUnlistenedDays': 0,
      'dailyBriefCatchUp.config.updatedAt': serverTimestamp(),
      'dailyBriefCatchUp.config.lastToggledAt': serverTimestamp(),
      'dailyBriefCatchUp.config.lastToggledBy': 'user'
    };

    // ✅ If enabling, clear all auto-disable flags
    if (enabled) {
      updates['dailyBriefCatchUp.config.disabledAt'] = deleteField();
      updates['dailyBriefCatchUp.config.disabledBy'] = deleteField();
      updates['dailyBriefCatchUp.config.autoDisableReason'] = deleteField();
    }

    await updateDoc(userRef, updates);

    console.log(`✅ Daily Brief ${enabled ? 'enabled' : 'disabled'}`);
    return { success: true, enabled };
  } catch (error) {
    console.error('❌ Failed to update enabled status:', error);
    throw error;
  }
}

/**
 * Get today's Daily Brief CatchUp
 */
async getTodaysDailyBriefCatchUp() {
  if (!this.currentUser) {
    console.warn('No user authenticated');
    return null;
  }

  try {
    // Get user's timezone
    const userRef = doc(db, 'users', this.currentUser.uid);
    const userDoc = await getDoc(userRef);
    const userTimezone = userDoc.data()?.timezone || 'America/New_York';
    
    // Get today's date in user's timezone (MM/DD/YYYY format)
    const now = new Date();
    const todayString = now.toLocaleDateString('en-US', { 
      timeZone: userTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    
    console.log(`📅 Checking for Daily Brief CatchUp on ${todayString} (${userTimezone})`);
    
    // ✅ NEW PATH: users/{userId}/dailyBriefCatchUp
    const briefsRef = collection(db, 'users', this.currentUser.uid, 'dailyBriefCatchUp');
    const q = query(
      briefsRef,
      where('generatedFor', '==', todayString),
      orderBy('generatedAt', 'desc'),
      limit(1)
    );
    
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      const briefDoc = snapshot.docs[0];
      const briefData = briefDoc.data();
      
      console.log('✅ Found Daily Brief CatchUp:', briefData.title);
      
      return {
        id: briefDoc.id,
        ...briefData
      };
    }
    
    console.log('📭 No Daily Brief CatchUp found for today');
    return null;
    
  } catch (error) {
    console.error('❌ Error getting today\'s Daily Brief CatchUp:', error);
    return null;
  }
}


/**
 * Get Daily Brief status (alias for compatibility)
 * This is used by the frontend to check brief status
 */
async getDailyBriefStatus() {
  if (!this.currentUser) {
    return null;
  }

  try {
    const userRef = doc(db, 'users', this.currentUser.uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return null;
    }
    
    const status = userDoc.data()?.dailyBriefCatchUp?.status || null;
    
    console.log('📊 Daily Brief Status:', status);
    return status;
  } catch (error) {
    console.error('❌ Error getting daily brief status:', error);
    return null;
  }
}
/**
 * Clear generation flag (for stuck states)
 */
async clearDailyBriefCatchUpGenerationFlag() {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const userRef = doc(db, 'users', this.currentUser.uid);
    
    await updateDoc(userRef, {
      'dailyBriefCatchUp.status.isGenerating': false,
      'dailyBriefCatchUp.status.startedAt': null
    });
    
    console.log('✅ Generation flag cleared');
  } catch (error) {
    console.error('❌ Error clearing flag:', error);
  }
}
/**
 * Mark Daily Brief CatchUp as listened
 */
async markDailyBriefCatchUpAsListened(briefId) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    console.log(`✅ Marking Daily Brief CatchUp ${briefId} as listened`);
    
    // ✅ NEW PATH: users/{userId}/dailyBriefCatchUp/{briefId}
    const briefRef = doc(db, 'users', this.currentUser.uid, 'dailyBriefCatchUp', briefId);
    
    await updateDoc(briefRef, {
      listened: true,
      playCount: increment(1),
      lastPlayedAt: serverTimestamp()
    });
    
    console.log('✅ Daily Brief CatchUp marked as listened');
  } catch (error) {
    console.error('❌ Error marking as listened:', error);
    throw error;
  }
}



/**
 * Get Daily Brief CatchUp history
 */
async getDailyBriefCatchUpHistory(limitCount = 7) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    // ✅ NEW PATH: users/{userId}/dailyBriefCatchUp
    const briefsRef = collection(db, 'users', this.currentUser.uid, 'dailyBriefCatchUp');
    
    const q = query(
      briefsRef,
      orderBy('generatedAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    
    const briefs = [];
    snapshot.forEach((doc) => {
      briefs.push({
        id: doc.id,
        ...doc.data()
      });
    });

    console.log(`✅ Retrieved ${briefs.length} Daily Brief CatchUp briefs`);
    return briefs;
  } catch (error) {
    console.error('❌ Failed to get Daily Brief CatchUp history:', error);
    return [];
  }
}


/**
 * Delete Daily Brief CatchUp
 */
async deleteDailyBriefCatchUp(briefId) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    // ✅ NEW PATH: users/{userId}/dailyBriefCatchUp/{briefId}
    const briefRef = doc(db, 'users', this.currentUser.uid, 'dailyBriefCatchUp', briefId);

    await deleteDoc(briefRef);
    console.log('✅ Daily Brief CatchUp deleted:', briefId);
    
    return true;
  } catch (error) {
    console.error('❌ Failed to delete Daily Brief CatchUp:', error);
    throw error;
  }
}


/**
 * 🧪 Admin: Test generate daily brief (30 seconds)
 */
async adminTestDailyBriefCatchUp() {
  if (!this.isAdmin()) {
    throw new Error('Admin access required');
  }

  try {
    console.log('🧪 Admin triggering 30-second test generation...');
    
    const response = await fetch(
      'https://us-central1-fir-7c9c9.cloudfunctions.net/adminForceGenerateDailyBriefCatchUp',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await this.currentUser.getIdToken()}`
        },
        body: JSON.stringify({
          userId: this.currentUser.uid
        })
      }
    );

    // ✅ FIX: Handle both JSON and text responses
    let errorData;
    const contentType = response.headers.get('content-type');
    
    if (!response.ok) {
      if (contentType && contentType.includes('application/json')) {
        errorData = await response.json();
      } else {
        // It's plain text (like "Internal Server Error")
        const textError = await response.text();
        throw new Error(`Server error (${response.status}): ${textError}`);
      }
      throw new Error(errorData.error || 'Failed to trigger generation');
    }

    // Parse successful response
    const result = await response.json();
    console.log('✅ Test generation triggered:', result);
    
    return result;
  } catch (error) {
    console.error('❌ Failed to trigger test generation:', error);
    throw error;
  }
}
/**
 * Save quiz attempt to history
 */
async saveQuizHistory(quizHistoryData) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const historyRef = collection(
      db,
      'users',
      this.currentUser.uid,
      'quizHistory'
    );

    const docData = {
      userId: this.currentUser.uid,
      quizId: quizHistoryData.quizId,
      quizTitle: quizHistoryData.quizTitle,
      quizDifficulty: quizHistoryData.quizDifficulty,
      totalQuestions: quizHistoryData.totalQuestions,
      correctAnswers: quizHistoryData.correctAnswers,
      percentage: quizHistoryData.percentage,
      timeSpent: quizHistoryData.timeSpent,
      completedAt: serverTimestamp(),
      questionPerformance: quizHistoryData.questionPerformance || []
    };

    const docRef = await addDoc(historyRef, docData);
    console.log('✅ Quiz history saved:', docRef.id);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('❌ Failed to save quiz history:', error);
    throw error;
  }
}
async saveFlashcardSession(setId, sessionData) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    // ✅ CRITICAL: Validate all required fields exist and are NOT undefined
    if (!sessionData.setTitle || sessionData.setTitle === undefined) {
      throw new Error('setTitle is required');
    }
    if (sessionData.cardsStudied === undefined || sessionData.cardsStudied === null) {
      throw new Error('cardsStudied is required');
    }
    if (sessionData.timeSpent === undefined || sessionData.timeSpent === null) {
      throw new Error('timeSpent is required');
    }

    // ✅ Use the CORRECT collection path
    const historyRef = collection(
      db,
      'users',
      this.currentUser.uid,
      'flashcardHistory'  // ✅ NOT flashcardSessions
    );

    // ✅ ONLY these fields - NO extras, NO undefined values
    const docData = {
      userId: this.currentUser.uid,
      setId: setId || '',
      setTitle: sessionData.setTitle,
      setDifficulty: sessionData.setDifficulty || 'medium',
      cardsStudied: sessionData.cardsStudied,
      timeSpent: sessionData.timeSpent,
      completedAt: serverTimestamp()
      // ✅ DO NOT include: cardsCompleted, cardInteractions, etc.
    };

    console.log('💾 Saving flashcard session with data:', docData);

    const docRef = await addDoc(historyRef, docData);
    
    console.log('✅ Flashcard session saved successfully:', docRef.id);
    
    return { 
      success: true, 
      id: docRef.id,
      data: docData 
    };
  } catch (error) {
    console.error('❌ Failed to save flashcard session:', error);
    console.error('Error details:', {
      message: error.message,
      sessionData: sessionData
    });
    throw error;
  }
}

/**
 * Get quiz history - FIXED METHOD
 */
async getQuizHistory(limitCount = 50) {
  if (!this.currentUser) {
    console.error('❌ User not authenticated');
    return [];
  }

  try {
    const historyRef = collection(
      db,
      'users',
      this.currentUser.uid,
      'quizHistory'
    );
    
    const q = query(
      historyRef,
      orderBy('completedAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    
    const history = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        completedAt: data.completedAt?.toDate?.() || new Date(),
        score: data.percentage || 0
      };
    });
    
    console.log(`✅ Loaded ${history.length} quiz attempts`);
    return history;
  } catch (error) {
    console.error('❌ Failed to get quiz history:', error);
    return [];
  }
}

/**
 * Get flashcard history - FIXED METHOD
 */
async getFlashcardHistory(limitCount = 50) {
  if (!this.currentUser) {
    console.error('❌ User not authenticated');
    return [];
  }

  try {
    const historyRef = collection(
      db,
      'users',
      this.currentUser.uid,
      'flashcardHistory'
    );
    
    const q = query(
      historyRef,
      orderBy('completedAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    
    const history = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        completedAt: data.completedAt?.toDate?.() || new Date()
      };
    });
    
    console.log(`✅ Loaded ${history.length} flashcard sessions`);
    return history;
  } catch (error) {
    console.error('❌ Failed to get flashcard history:', error);
    return [];
  }
}
// ============================================
// STUDYHUB METHODS - Add to existing service
// ============================================

/**
 * Save quiz attempt to Firestore
 */
/**
 * Save quiz attempt to Firestore
 */
async saveQuizAttempt(quizId, quizData, results) {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    // ✅ CORRECT PATH - matches security rules
    const attemptRef = collection(
      db,
      'users',
      this.currentUser.uid,
      'quizAttempts'  // ✅ Direct subcollection under users/{userId}
    );

    const attemptData = {
      quizId: quizId,
      quizTitle: quizData.title,
      quizDifficulty: quizData.difficulty,
      
      // Results
      totalQuestions: results.totalQuestions,
      correctAnswers: results.correctAnswers,
      incorrectAnswers: results.incorrectAnswers,
      score: results.score,
      percentage: results.percentage,
      
      // Performance tracking
      questionPerformance: results.questionPerformance,
      timeSpent: results.timeSpent,
      
      // User answers
      userAnswers: results.userAnswers,
      
      // Metadata
      completedAt: serverTimestamp(),
      userId: this.currentUser.uid,
      preptuneUserId: this.preptuneUserId
    };

    const docRef = await addDoc(attemptRef, attemptData);
    
    // Update quiz statistics
    await this.updateQuizStatistics(quizId, results);
    
    console.log('✅ Quiz attempt saved:', docRef.id);
    return { id: docRef.id, ...attemptData };
  } catch (error) {
    console.error('❌ Failed to save quiz attempt:', error);
    throw error;
  }
}
/**
 * Update quiz statistics (total attempts, average score, etc.)
 */
async updateQuizStatistics(quizId, results) {
  if (!this.currentUser) return;

  try {
    // ✅ CORRECT PATH - use generatedQuizzes directly
    const statsRef = doc(
      db,
      'users',
      this.currentUser.uid,
      'generatedQuizzes',  // ✅ Store stats in the quiz document itself
      quizId
    );

    const statsDoc = await getDoc(statsRef);
    
    if (statsDoc.exists()) {
      const currentStats = statsDoc.data();
      const currentAvg = currentStats.averageScore || 0;
      const currentAttempts = currentStats.completionCount || 0;
      
      await updateDoc(statsRef, {
        completionCount: increment(1),
        averageScore: ((currentAvg * currentAttempts) + results.percentage) / (currentAttempts + 1),
        lastAttemptAt: serverTimestamp()
      });
    } else {
      // Quiz doesn't exist yet, create it with stats
      await setDoc(statsRef, {
        completionCount: 1,
        averageScore: results.percentage,
        lastAttemptAt: serverTimestamp()
      }, { merge: true });
    }
  } catch (error) {
    console.error('Failed to update quiz statistics:', error);
  }
}



/**
 * Update flashcard set statistics
 */
async updateFlashcardStatistics(setId, sessionData) {
  if (!this.currentUser) return;

  try {
    // ✅ CORRECT PATH - store stats in the flashcard set document itself
    const statsRef = doc(
      db,
      'users',
      this.currentUser.uid,
      'generatedFlashcards',  // ✅ Update the flashcard set directly
      setId
    );

    const statsDoc = await getDoc(statsRef);
    
    if (statsDoc.exists()) {
      await updateDoc(statsRef, {
        studyCount: increment(1),
        totalTimeSpent: increment(sessionData.timeSpent),
        lastStudiedAt: serverTimestamp()
      });
    } else {
      // Flashcard set doesn't exist yet, create it with stats
      await setDoc(statsRef, {
        studyCount: 1,
        totalTimeSpent: sessionData.timeSpent,
        lastStudiedAt: serverTimestamp()
      }, { merge: true });
    }
  } catch (error) {
    console.error('Failed to update flashcard statistics:', error);
  }
}

/**
 * Track Ask Preptune interaction (AI help during quiz/flashcard)
 */
async trackAskPreptuneInteraction(interaction) {
  if (!this.currentUser) return;

  try {
    // ✅ CORRECT PATH - use studyConversations as defined in security rules
    const interactionRef = collection(
      db,
      'users',
      this.currentUser.uid,
      'studyConversations'  // ✅ Matches your security rules
    );

    const interactionData = {
      type: interaction.type, // 'quiz' or 'flashcard'
      contentId: interaction.contentId,
      questionText: interaction.questionText,
      userQuestion: interaction.userQuestion,
      aiResponse: interaction.aiResponse,
      helpful: interaction.helpful || null,
      timestamp: serverTimestamp(),
      userId: this.currentUser.uid,
      preptuneUserId: this.preptuneUserId
    };

    await addDoc(interactionRef, interactionData);
    console.log('✅ Ask Preptune interaction tracked');
  } catch (error) {
    console.error('Failed to track Ask Preptune interaction:', error);
  }
}

/**
 * ✅ NEW: Get daily brief generation status for background checking
 * This is specifically for the background notification system
 */
async getDailyBriefCatchUpGenerationStatus() {
  if (!this.currentUser) {
    return null;
  }

  try {
    const userRef = doc(db, 'users', this.currentUser.uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return null;
    }

    const status = userDoc.data()?.dailyBriefCatchUp?.status || {};

    return {
      isGenerating: status.isGenerating || false,
      lastGenerated: status.lastGenerated,
      lastGeneratedDate: status.lastGeneratedDate,
      
      // ✅ NEW: These fields are for background notifications
      newBriefAvailable: status.newBriefAvailable || false,
      newBriefDate: status.newBriefDate || null,
      newBriefGeneratedAt: status.newBriefGeneratedAt || null
    };

  } catch (error) {
    console.error('Failed to get generation status:', error);
    return null;
  }
}

/**
 * ✅ NEW: Clear the new brief flag (called after notification is shown)
 */
async clearNewBriefFlag() {
  if (!this.currentUser) {
    return;
  }

  try {
    const userRef = doc(db, 'users', this.currentUser.uid);
    
    await updateDoc(userRef, {
      'dailyBriefCatchUp.status.newBriefAvailable': false
    });

    console.log('✅ New brief flag cleared');

  } catch (error) {
    console.error('Failed to clear new brief flag:', error);
  }
}


/**
 * Listen for new brief availability
 */
listenForNewBrief(callback) {
  if (!this.isReady()) {
    console.warn('Firestore not ready');
    return () => {};
  }

  const userId = this.currentUserId;
  if (!userId) {
    console.warn('No user ID');
    return () => {};
  }

  console.log('👂 Setting up real-time listener for brief status');

  const unsubscribe = this.db
    .collection('users')
    .doc(userId)
    .onSnapshot(
      (doc) => {
        if (!doc.exists) return;

        const userData = doc.data();
        const status = userData?.dailyBriefCatchUp?.status;

        if (!status) return;

        callback({
          newBriefAvailable: status.newBriefAvailable || false,
          newBriefDate: status.newBriefDate || null,
          newBriefGeneratedAt: status.newBriefGeneratedAt || null,
          isGenerating: status.isGenerating || false
        });
      },
      (error) => {
        console.error('Listener error:', error);
      }
    );

  return unsubscribe;
}
/**
 * Get user's flashcard history
 */
async getFlashcardHistory(limitCount = 20) {
  if (!this.currentUser) {
    console.error('❌ User not authenticated');
    return [];
  }

  try {
    // ✅ Use imported 'db' instead of 'this.db'
    const sessionsRef = collection(db, 'users', this.currentUser.uid, 'flashcardHistory');
    const q = query(sessionsRef, orderBy('completedAt', 'desc'), limit(limitCount));
    const snapshot = await getDocs(q);
    
    const history = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      completedAt: doc.data().completedAt?.toDate() || new Date()
    }));
    
    console.log(`✅ Loaded ${history.length} flashcard sessions`);
    return history;
  } catch (error) {
    console.error('❌ Failed to get flashcard history:', error);
    return [];
  }
}
/**
 * Get overall study statistics
 */
async getStudyStatistics() {
  if (!this.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    const [quizHistory, flashcardHistory] = await Promise.all([
      this.getQuizHistory(100),
      this.getFlashcardHistory(100)
    ]);

    // Calculate quiz stats
    const quizStats = {
      totalAttempts: quizHistory.length,
      averageScore: quizHistory.length > 0
        ? Math.round(quizHistory.reduce((sum, q) => sum + q.percentage, 0) / quizHistory.length)
        : 0,
      totalQuestionsAnswered: quizHistory.reduce((sum, q) => sum + q.totalQuestions, 0),
      totalCorrectAnswers: quizHistory.reduce((sum, q) => sum + q.correctAnswers, 0),
      totalTimeSpent: quizHistory.reduce((sum, q) => sum + (q.timeSpent || 0), 0)
    };

    // Calculate flashcard stats
    const flashcardStats = {
      totalSessions: flashcardHistory.length,
      totalCardsStudied: flashcardHistory.reduce((sum, s) => sum + s.cardsStudied, 0),
      totalTimeSpent: flashcardHistory.reduce((sum, s) => sum + s.timeSpent, 0)
    };

    return {
      quizStats,
      flashcardStats,
      lastActivity: quizHistory[0]?.completedAt || flashcardHistory[0]?.completedAt || null
    };
  } catch (error) {
    console.error('Failed to get study statistics:', error);
    throw error;
  }
}
/**
 * ✅ FIXED: Get shared content by shareId
 * This is accessible by anyone with the link - no authentication required
 * 
 * @param {string} shareId - The share ID from the URL
 * @returns {Promise<Object|null>} The shared content or null if not found
 */
async getSharedContent(shareId) {
  try {
    console.log('📥 Loading shared content:', shareId);

    const sharedDocRef = doc(db, 'sharedContent', shareId);
    const docSnap = await getDoc(sharedDocRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      
      console.log('📦 Raw Firestore data:', data);
      console.log('📦 Type field:', data.type);
      console.log('📦 Title field:', data.title);
      console.log('📦 Questions field:', data.questions);
      console.log('📦 Flashcards field:', data.flashcards);
      
      // Check if content is still active
      if (data.isActive === false) {
        console.warn('⚠️ Content is no longer active:', shareId);
        return null;
      }

      // Increment view count (optional - do this async to not block)
      this._incrementViewCount(shareId).catch(err => 
        console.warn('Failed to increment view count:', err)
      );

      console.log('✅ Shared content loaded:', data.title);
      console.log('   Type:', data.type);
      console.log('   Difficulty:', data.difficulty);
      
      // ✅ CRITICAL: Return the data in the EXACT format expected
      const result = {
        id: shareId,
        type: data.type,
        title: data.title,
        description: data.description || '',
        difficulty: data.difficulty || 'medium',
        // Include BOTH fields regardless of type (viewer will filter)
        questions: data.questions || [],
        flashcards: data.flashcards || [],
        // Metadata
        createdBy: data.createdBy,
        createdAt: data.createdAt,
        viewCount: data.viewCount || 0
      };

      console.log('✅ Returning formatted result:', {
        type: result.type,
        title: result.title,
        hasQuestions: result.questions.length > 0,
        hasFlashcards: result.flashcards.length > 0
      });

      return result;
    } else {
      console.error('❌ Shared content not found:', shareId);
      return null;
    }

  } catch (error) {
    console.error('❌ Failed to get shared content:', error);
    console.error('Error details:', error.message);
    return null;
  }
}

/**
 * ✅ Increment view count for shared content (private helper)
 * This runs async and doesn't block the main flow
 */
async _incrementViewCount(shareId) {
  try {
    const sharedDocRef = doc(db, 'sharedContent', shareId);
    
    await updateDoc(sharedDocRef, {
      viewCount: increment(1),
      lastAccessedAt: serverTimestamp()
    });
    
    console.log('📊 View count incremented for:', shareId);
  } catch (error) {
    // Don't throw - this is optional metadata
    console.warn('⚠️ Could not increment view count:', error.message);
  }
}

// ===== HELPER METHODS =====

/**
 * Generate tags for podcast
 */
generatePodcastTags(podcastData) {
  const tags = [];
  
  if (podcastData.type) tags.push(podcastData.type);
  if (podcastData.modulesUsed) tags.push(...podcastData.modulesUsed);
  if (podcastData.youtubeVideo) tags.push('youtube');
  
  return tags;
}

  // Save journal entry with proper validation
  async saveJournalEntry(journalData) {
    if (!this.currentUser || !this.preptuneUserId) {
      throw new Error('User not authenticated or no Preptune ID');
    }

    try {
      const processedData = {
        preptuneUserId: this.preptuneUserId,
        userId: this.currentUser.uid,
        
        header: journalData.header || 'Journal Entry',
        summary: journalData.summary || 'No summary available',
        insights: journalData.insights || 'No insights available', 
        conversation: Array.isArray(journalData.conversation) ? journalData.conversation : [],
        
        messageCount: journalData.messageCount || (Array.isArray(journalData.conversation) ? journalData.conversation.length : 0),
        sessionStartTime: journalData.sessionStartTime ? new Date(journalData.sessionStartTime) : serverTimestamp(),
        voiceName: journalData.voiceName || 'Unknown Voice',
        savedManually: journalData.savedManually || false,
        hasGeneratedSummary: journalData.hasGeneratedSummary || false,
        hasEmotionalInsights: journalData.hasEmotionalInsights || false,
        
        savedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      };

      const journalsRef = collection(db, 'users', this.currentUser.uid, 'journals');
      const docRef = await addDoc(journalsRef, processedData);
      
      console.log('Journal entry saved to Firestore:', docRef.id);
      
      return {
        journalId: docRef.id,
        ...processedData,
        savedAt: new Date().toISOString(),
        createdAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Failed to save journal entry:', error);
      throw error;
    }
  }

  // Update session statistics
  async updateUserStats() {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      const userDocRef = doc(db, 'users', this.currentUser.uid);
      const userData = await this.getUserData();
      
      const newStats = {
        'stats.totalConversations': (userData.stats?.totalConversations || 0) + 1,
        'stats.totalMessages': (userData.stats?.totalMessages || 0) + 1,
        'stats.lastCallTime': serverTimestamp()
      };

      await updateDoc(userDocRef, newStats);
      console.log('User stats updated');
    } catch (error) {
      console.error('Failed to update user stats:', error);
    }
  }

  // FIXED: Get call timer with proper remaining time calculation
  async getCallTimer() {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      const userData = await this.getUserData();
      const trialStatus = this.calculateTrialStatus(userData);
      const timerData = userData.callTimer || {};
      
      // For admin users, return unlimited timer
      if (this.isAdmin()) {
        return {
          totalTime: Infinity,
          remainingTime: Infinity,
          lastPaused: null,
          isActive: timerData.isActive || false,
          sessionStartTime: timerData.sessionStartTime || null,
          resetCount: 0,
          lastResetBy: null,
          lastResetAt: null,
          trialStatus: trialStatus,
          isAdmin: true
        };
      }
      
      // FIXED: Use the actual remainingTime from database, don't recalculate
      const remainingSeconds = timerData.remainingTime !== undefined ? timerData.remainingTime : (this.TOTAL_TRIAL_MINUTES * 60);
      const totalSecondsAllowed = this.TOTAL_TRIAL_MINUTES * 60;
      
      const defaultTimer = {
        totalTime: totalSecondsAllowed,
        remainingTime: remainingSeconds, // FIXED: Use stored remaining time
        lastPaused: timerData.lastPaused || null,
        isActive: timerData.isActive || false,
        sessionStartTime: timerData.sessionStartTime || null,
        resetCount: timerData.resetCount || 0,
        lastResetBy: timerData.lastResetBy || null,
        lastResetAt: timerData.lastResetAt || null,
        trialStatus: trialStatus,
        isAdmin: false
      };

      console.log('Call timer data:', {
        storedRemainingTime: timerData.remainingTime,
        returnedRemainingTime: remainingSeconds,
        remainingMinutes: remainingSeconds / 60
      });

      return defaultTimer;
    } catch (error) {
      console.error('Failed to get call timer:', error);
      throw error;
    }
  }

  // Update call timer
  async updateCallTimer(timerData) {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      const userDocRef = doc(db, 'users', this.currentUser.uid);
      await updateDoc(userDocRef, {
        'callTimer': {
          ...timerData,
          lastUpdated: serverTimestamp()
        },
        lastActiveAt: serverTimestamp()
      });
      
      console.log('Call timer updated:', timerData);
    } catch (error) {
      console.error('Failed to update call timer:', error);
      throw error;
    }
  }

  // Reset call timer (admin only - resets entire trial)
  async resetCallTimer(adminUserId = null) {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    // Only admins can reset trial usage
    if (!this.isAdmin() && adminUserId !== this.currentUser.uid) {
      throw new Error('Only administrators can reset trial usage');
    }

    try {
      const resetData = {
        totalTime: this.TOTAL_TRIAL_MINUTES * 60,
        remainingTime: this.TOTAL_TRIAL_MINUTES * 60,
        lastPaused: null,
        isActive: false,
        sessionStartTime: null,
        resetCount: 0,
        lastResetBy: adminUserId,
        lastResetAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      };

      const userDocRef = doc(db, 'users', this.currentUser.uid);
      
      // Reset both timer and usage
      await updateDoc(userDocRef, {
        'callTimer': resetData,
        'trialUsage.totalMinutesUsed': 0,
        'trialUsage.totalSecondsUsed': 0,
        'trialUsage.sessionsCount': 0,
        'trialUsage.lastSessionDuration': 0,
        'trialUsage.dailyUsage': {},
        lastActiveAt: serverTimestamp()
      });
      
      console.log('Trial usage reset by:', adminUserId || 'admin');
      return resetData;
    } catch (error) {
      console.error('Failed to reset trial usage:', error);
      throw error;
    }
  }

  // Check if current user is admin
  isAdmin() {
    if (!this.currentUser?.email) return false;
    return this.adminEmails.includes(this.currentUser.email.toLowerCase());
  }

  // Get all users (admin only)
  async getAllUsers() {
    if (!this.isAdmin()) {
      throw new Error('Insufficient permissions - admin required');
    }

    try {
      console.log('Admin fetching all users...');
      const usersRef = collection(db, 'users');
      const querySnapshot = await getDocs(usersRef);
      
      const users = [];
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        const trialStatus = this.calculateTrialStatus(userData);
        
        users.push({
          uid: doc.id,
          ...userData,
          trialStatus
        });
      });

      console.log('Fetched', users.length, 'users');
      return users;
    } catch (error) {
      console.error('Failed to get all users:', error);
      throw error;
    }
  }

  // Admin reset any user's timer
  async adminResetUserTimer(targetUserId, adminUserId) {
    if (!this.isAdmin()) {
      throw new Error('Insufficient permissions - admin required');
    }

    try {
      console.log('Admin resetting timer for user:', targetUserId);
      
      const targetUserRef = doc(db, 'users', targetUserId);
      const resetData = {
        totalTime: this.TOTAL_TRIAL_MINUTES * 60,
        remainingTime: this.TOTAL_TRIAL_MINUTES * 60,
        lastPaused: null,
        isActive: false,
        sessionStartTime: null,
        resetCount: 0,
        lastResetBy: adminUserId,
        lastResetAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      };

      // Reset both timer and trial usage
      await updateDoc(targetUserRef, {
        'callTimer': resetData,
        'trialUsage.totalMinutesUsed': 0,
        'trialUsage.totalSecondsUsed': 0,
        'trialUsage.sessionsCount': 0,
        'trialUsage.lastSessionDuration': 0,
        'trialUsage.dailyUsage': {},
        lastActiveAt: serverTimestamp()
      });
      
      console.log('Admin timer and trial reset completed for user:', targetUserId);
      
      await this.logAdminAction({
        action: 'trial_reset',
        targetUserId: targetUserId,
        adminUserId: adminUserId,
        performedBy: this.currentUser.email
      });
      
      return resetData;
    } catch (error) {
      console.error('Failed to admin reset timer:', error);
      throw error;
    }
  }

  // Admin extend user trial (add more days)
  async adminExtendUserTrial(targetUserId, additionalDays, adminUserId) {
    if (!this.isAdmin()) {
      throw new Error('Insufficient permissions - admin required');
    }

    try {
      console.log(`Admin extending trial for user ${targetUserId} by ${additionalDays} days`);
      
      const targetUserRef = doc(db, 'users', targetUserId);
      const userSnap = await getDoc(targetUserRef);
      const userData = userSnap.data();
      
      const currentCreatedAt = userData.createdAt?.toDate ? userData.createdAt.toDate() : new Date(userData.createdAt);
      const newCreatedAt = new Date(currentCreatedAt);
      newCreatedAt.setDate(newCreatedAt.getDate() - additionalDays); // Extend by moving creation date back

      await updateDoc(targetUserRef, {
        createdAt: newCreatedAt,
        lastActiveAt: serverTimestamp(),
        'trialStatus.extendedBy': adminUserId,
        'trialStatus.extendedAt': serverTimestamp(),
        'trialStatus.additionalDays': (userData.trialStatus?.additionalDays || 0) + additionalDays
      });
      
      await this.logAdminAction({
        action: 'trial_extended',
        targetUserId: targetUserId,
        adminUserId: adminUserId,
        performedBy: this.currentUser.email,
        additionalDays: additionalDays
      });
      
      console.log(`Trial extended successfully by ${additionalDays} days`);
      return { success: true, additionalDays };
    } catch (error) {
      console.error('Failed to extend trial:', error);
      throw error;
    }
  }

  // Admin remove user completely
  async adminRemoveUser(targetUserId, adminUserId) {
    if (!this.isAdmin()) {
      throw new Error('Insufficient permissions - admin required');
    }

    try {
      console.log('Admin removing user completely:', targetUserId);
      
      const targetUserRef = doc(db, 'users', targetUserId);
      const userSnap = await getDoc(targetUserRef);
      const userEmail = userSnap.exists() ? userSnap.data().email : 'Unknown';

      const batch = writeBatch(db);

      // Delete all user's conversations
      const conversationsRef = collection(db, 'users', targetUserId, 'conversations');
      const conversationsSnapshot = await getDocs(conversationsRef);
      console.log('Deleting', conversationsSnapshot.size, 'conversations...');
      
      conversationsSnapshot.forEach((conversationDoc) => {
        batch.delete(conversationDoc.ref);
      });

      // Delete all user's journals
      const journalsRef = collection(db, 'users', targetUserId, 'journals');
      const journalsSnapshot = await getDocs(journalsRef);
      console.log('Deleting', journalsSnapshot.size, 'journal entries...');
      
      journalsSnapshot.forEach((journalDoc) => {
        batch.delete(journalDoc.ref);
      });

      // Delete user document
      batch.delete(targetUserRef);

      // Execute batch deletion
      await batch.commit();
      
      console.log('User completely removed:', targetUserId);
      
      // Log admin action
      await this.logAdminAction({
        action: 'user_removed',
        targetUserId: targetUserId,
        targetUserEmail: userEmail,
        adminUserId: adminUserId,
        performedBy: this.currentUser.email,
        deletedData: {
          conversations: conversationsSnapshot.size,
          journals: journalsSnapshot.size
        }
      });
      
      return {
        success: true,
        deletedConversations: conversationsSnapshot.size,
        deletedJournals: journalsSnapshot.size,
        userEmail: userEmail
      };
    } catch (error) {
      console.error('Failed to remove user:', error);
      throw error;
    }
  }

  // Log admin actions
  async logAdminAction(actionData) {
    try {
      const adminLogsRef = collection(db, 'adminLogs');
      await addDoc(adminLogsRef, {
        ...actionData,
        timestamp: serverTimestamp(),
        adminEmail: this.currentUser.email
      });
      console.log('Admin action logged:', actionData.action);
    } catch (error) {
      console.error('Failed to log admin action:', error);
    }
  }

  // Get admin logs (admin only)
  async getAdminLogs(limitCount = 50) {
    if (!this.isAdmin()) {
      throw new Error('Insufficient permissions - admin required');
    }

    try {
      const adminLogsRef = collection(db, 'adminLogs');
      const q = query(adminLogsRef, orderBy('timestamp', 'desc'), limit(limitCount));
      const querySnapshot = await getDocs(q);
      
      const logs = [];
      querySnapshot.forEach((doc) => {
        logs.push({
          id: doc.id,
          ...doc.data()
        });
      });

      console.log('Fetched', logs.length, 'admin logs');
      return logs;
    } catch (error) {
      console.error('Failed to get admin logs:', error);
      return [];
    }
  }

  // Get user by ID (admin only)
  async getUserById(userId) {
    if (!this.isAdmin()) {
      throw new Error('Insufficient permissions - admin required');
    }

    try {
      const userDocRef = doc(db, 'users', userId);
      const userDocSnap = await getDoc(userDocRef);
      
      if (!userDocSnap.exists()) {
        throw new Error('User not found');
      }

      const userData = userDocSnap.data();
      const trialStatus = this.calculateTrialStatus(userData);

      return {
        uid: userId,
        ...userData,
        trialStatus
      };
    } catch (error) {
      console.error('Failed to get user by ID:', error);
      throw error;
    }
  }

  // Update any user's data (admin only)
  async adminUpdateUser(userId, updates) {
    if (!this.isAdmin()) {
      throw new Error('Insufficient permissions - admin required');
    }

    try {
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, {
        ...updates,
        lastModifiedBy: this.currentUser.email,
        lastModifiedAt: serverTimestamp()
      });

      await this.logAdminAction({
        action: 'user_update',
        targetUserId: userId,
        adminUserId: this.currentUser.uid,
        performedBy: this.currentUser.email,
        updatedFields: Object.keys(updates)
      });

      console.log('Admin updated user:', userId);
    } catch (error) {
      console.error('Failed to admin update user:', error);
      throw error;
    }
  }

  // Get user conversations (admin only)
  async getUserConversations(userId, limitCount = 10) {
    if (!this.isAdmin()) {
      throw new Error('Insufficient permissions - admin required');
    }

    try {
      const conversationsRef = collection(db, 'users', userId, 'conversations');
      const q = query(conversationsRef, orderBy('savedAt', 'desc'), limit(limitCount));
      const querySnapshot = await getDocs(q);
      
      const conversations = [];
      querySnapshot.forEach((doc) => {
        conversations.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return conversations;
    } catch (error) {
      console.error('Failed to get user conversations:', error);
      return [];
    }
  }

  // Get conversation messages (admin only)
  async getConversationMessages(conversationId, userId = null) {
    if (!this.isAdmin()) {
      throw new Error('Insufficient permissions - admin required');
    }

    try {
      if (!userId) {
        const users = await this.getAllUsers();
        for (const user of users) {
          try {
            const conversationRef = doc(db, 'users', user.uid, 'conversations', conversationId);
            const conversationSnap = await getDoc(conversationRef);
            if (conversationSnap.exists()) {
              userId = user.uid;
              break;
            }
          } catch (error) {
            continue;
          }
        }
        
        if (!userId) {
          throw new Error('Conversation not found');
        }
      }

      const conversationRef = doc(db, 'users', userId, 'conversations', conversationId);
      const conversationSnap = await getDoc(conversationRef);
      
      if (!conversationSnap.exists()) {
        throw new Error('Conversation not found');
      }

      const conversationData = conversationSnap.data();
      return conversationData.conversation || conversationData.messages || [];
      
    } catch (error) {
      console.error('Failed to get conversation messages:', error);
      return [];
    }
  }

  // Get conversation messages for specific user (admin only)
  async getUserConversationMessages(userId, conversationId) {
    if (!this.isAdmin()) {
      throw new Error('Insufficient permissions - admin required');
    }

    try {
      const conversationRef = doc(db, 'users', userId, 'conversations', conversationId);
      const conversationSnap = await getDoc(conversationRef);
      
      if (!conversationSnap.exists()) {
        throw new Error('Conversation not found');
      }

      const conversationData = conversationSnap.data();
      return conversationData.conversation || conversationData.messages || [];
      
    } catch (error) {
      console.error('Failed to get user conversation messages:', error);
      return [];
    }
  }

  // Feedback popup
  async saveFeedback(feedbackData) {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      const feedbackRef = collection(db, 'feedback');
      const docRef = await addDoc(feedbackRef, {
        ...feedbackData,
        createdAt: serverTimestamp(),
        userEmail: this.currentUser.email,
        userId: this.currentUser.uid,
        reviewed: false
      });

      console.log('Feedback saved to Firestore:', docRef.id);
      return { feedbackId: docRef.id, ...feedbackData };
      
    } catch (error) {
      console.error('Failed to save feedback to Firestore:', error);
      throw error;
    }
  }

  // Get all feedback entries (admin only)
  async getAllFeedback(limitCount = 50) {
    if (!this.isAdmin()) {
      throw new Error('Insufficient permissions - admin required');
    }

    try {
      console.log('Admin fetching all feedback...');
      const feedbackRef = collection(db, 'feedback');
      const q = query(feedbackRef, orderBy('createdAt', 'desc'), limit(limitCount));
      const querySnapshot = await getDocs(q);
      
      const feedback = [];
      querySnapshot.forEach((doc) => {
        feedback.push({
          id: doc.id,
          ...doc.data()
        });
      });

      console.log('Fetched', feedback.length, 'feedback entries');
      return feedback;
    } catch (error) {
      console.error('Failed to get feedback:', error);
      throw error;
    }
  }

  // Get feedback by user email (admin only)
  async getFeedbackByUser(userEmail, limitCount = 20) {
    if (!this.isAdmin()) {
      throw new Error('Insufficient permissions - admin required');
    }

    try {
      const feedbackRef = collection(db, 'feedback');
      const q = query(
        feedbackRef, 
        where('userEmail', '==', userEmail),
        orderBy('createdAt', 'desc'), 
        limit(limitCount)
      );
      const querySnapshot = await getDocs(q);
      
      const feedback = [];
      querySnapshot.forEach((doc) => {
        feedback.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return feedback;
    } catch (error) {
      console.error('Failed to get user feedback:', error);
      return [];
    }
  }
async adminMigrateOldData() {
  if (!this.isAdmin()) {
    throw new Error('Admin access required');
  }

  try {
    console.log('🔄 Starting data migration...');
    
    // Get old daily briefs from users/{uid}/dailyBriefs
    const oldDailyBriefsRef = collection(db, 'users', this.currentUser.uid, 'dailyBriefs');
    const oldBriefsSnapshot = await getDocs(oldDailyBriefsRef);
    
    if (oldBriefsSnapshot.empty) {
      console.log('✅ No old daily briefs to migrate');
      return { success: true, migrated: 0 };
    }
    
    console.log(`📦 Found ${oldBriefsSnapshot.size} old daily briefs to migrate`);
    
    // Move to new location
    let migratedCount = 0;
    for (const oldDoc of oldBriefsSnapshot.docs) {
      const data = oldDoc.data();
      
      // Save to new location
      const newDailyBriefRef = collection(
        db,
        'InnerloopPodcast',
        this.currentUser.uid,
        'dailyBriefs'
      );
      
      await addDoc(newDailyBriefRef, data);
      
      // Delete from old location
      await deleteDoc(oldDoc.ref);
      
      migratedCount++;
      console.log(`✅ Migrated brief ${migratedCount}/${oldBriefsSnapshot.size}`);
    }
    
    console.log(`✅ Migration complete! Moved ${migratedCount} daily briefs`);
    
    return { success: true, migrated: migratedCount };
  } catch (error) {
    console.error('❌ Migration failed:', error);
    throw error;
  }
}
  // Mark feedback as read/reviewed (admin only)
  async markFeedbackAsReviewed(feedbackId, adminUserId) {
    if (!this.isAdmin()) {
      throw new Error('Insufficient permissions - admin required');
    }

    try {
      const feedbackRef = doc(db, 'feedback', feedbackId);
      await updateDoc(feedbackRef, {
        reviewed: true,
        reviewedBy: adminUserId,
        reviewedAt: serverTimestamp(),
        reviewedByEmail: this.currentUser.email
      });

      await this.logAdminAction({
        action: 'feedback_reviewed',
        feedbackId: feedbackId,
        adminUserId: adminUserId,
        performedBy: this.currentUser.email
      });

      console.log('Feedback marked as reviewed:', feedbackId);
    } catch (error) {
      console.error('Failed to mark feedback as reviewed:', error);
      throw error;
    }
  }

  // Delete feedback entry (admin only)
  async deleteFeedback(feedbackId, adminUserId) {
    if (!this.isAdmin()) {
      throw new Error('Insufficient permissions - admin required');
    }

    try {
      const feedbackRef = doc(db, 'feedback', feedbackId);
      const feedbackSnap = await getDoc(feedbackRef);
      const feedbackData = feedbackSnap.exists() ? feedbackSnap.data() : null;
      
      await deleteDoc(feedbackRef);

      await this.logAdminAction({
        action: 'feedback_deleted',
        feedbackId: feedbackId,
        adminUserId: adminUserId,
        performedBy: this.currentUser.email,
        deletedFeedbackType: feedbackData?.feedbackType || 'unknown'
      });

      console.log('Feedback deleted:', feedbackId);
    } catch (error) {
      console.error('Failed to delete feedback:', error);
      throw error;
    }
  }

  // Get user journals (admin only)
  async getUserJournals(userId, limitCount = 10) {
    if (!this.isAdmin()) {
      throw new Error('Insufficient permissions - admin required');
    }

    try {
      const journalsRef = collection(db, 'users', userId, 'journals');
      const q = query(journalsRef, orderBy('savedAt', 'desc'), limit(limitCount));
      const querySnapshot = await getDocs(q);
      
      const journals = [];
      querySnapshot.forEach((doc) => {
        journals.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return journals;
    } catch (error) {
      console.error('Failed to get user journals:', error);
      return [];
    }
  }

  // Check beta user limit
  async checkBetaUserLimit() {
    try {
      console.log('Checking beta user limit...');
      
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      
      // Admin emails to exclude from beta count
      const adminEmails = [
        'franklyn@preptune.com',
        'kogolof1@montclair.edu',
        'support@preptune.com'
      ];
      
      const adminEmailsLower = adminEmails.map(email => email.toLowerCase());
      let betaUserCount = 0;
      
      // Count only non-admin users
      snapshot.forEach((doc) => {
        const userData = doc.data();
        const userEmail = userData.email?.toLowerCase();
        
        if (userEmail && !adminEmailsLower.includes(userEmail)) {
          betaUserCount++;
        }
      });
      
      console.log('Current beta users (excluding admins):', betaUserCount);
      
      const BETA_USER_LIMIT = 50;
      
      if (betaUserCount >= BETA_USER_LIMIT) {
        return {
          canSignup: false,
          currentCount: betaUserCount,
          limit: BETA_USER_LIMIT,
          message: 'Beta testing has reached its user limit. Contact support@preptune.com for special entry requests.'
        };
      }
      
      return {
        canSignup: true,
        currentCount: betaUserCount,
        limit: BETA_USER_LIMIT,
        spotsRemaining: BETA_USER_LIMIT - betaUserCount
      };
      
    } catch (error) {
      console.error('Failed to check beta user limit:', error);
      // In case of error, allow signup to avoid blocking legitimate users
      return {
        canSignup: true,
        currentCount: 0,
        limit: 50,
        error: 'Unable to verify user limit'
      };
    }
  }

  // Get beta user stats
  async getBetaUserStats() {
    if (!this.isAdmin()) {
      throw new Error('Insufficient permissions - admin required');
    }

    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      
      // Admin emails to exclude from beta count
      const adminEmails = [
        'franklyn@preptune.com',
        'kogolof1@montclair.edu', 
        'support@preptune.com'
      ];
      
      const adminEmailsLower = adminEmails.map(email => email.toLowerCase());
      let betaUserCount = 0;
      let totalUserCount = 0;
      let expiredUsers = 0;
      let activeUsers = 0;
      
      // Count users and filter out admins
      snapshot.forEach((doc) => {
        const userData = doc.data();
        const userEmail = userData.email?.toLowerCase();
        
        totalUserCount++;
        
        if (userEmail && !adminEmailsLower.includes(userEmail)) {
          betaUserCount++;
          
          // Check trial status
          const trialStatus = this.calculateTrialStatus(userData);
          if (trialStatus.isExpired) {
            expiredUsers++;
          } else {
            activeUsers++;
          }
        }
      });
      
      const BETA_USER_LIMIT = 50;
      
      return {
        totalUsers: betaUserCount, // Only beta users (non-admins)
        activeUsers: activeUsers,
        expiredUsers: expiredUsers,
        limit: BETA_USER_LIMIT,
        spotsRemaining: Math.max(0, BETA_USER_LIMIT - betaUserCount),
        percentageFull: Math.round((betaUserCount / BETA_USER_LIMIT) * 100),
        totalUsersIncludingAdmins: totalUserCount,
        adminCount: totalUserCount - betaUserCount
      };
    } catch (error) {
      console.error('Failed to get beta user stats:', error);
      throw error;
    }
  }

  // Add these methods to your FirestorePersistentStorageService class

// Initialize signup capacity document (run once to set up the collection)
async initializeSignupCapacity() {
  if (!this.isAdmin()) {
    throw new Error('Only administrators can initialize signup capacity');
  }

  try {
    console.log('Initializing signup capacity document...');
    
    const capacityRef = doc(db, 'signupCapacity', 'betaLimits');
    const capacityDoc = await getDoc(capacityRef);
    
    if (!capacityDoc.exists()) {
      const initialCapacityData = {
        totalCapacity: 50,
        currentSignups: 0,
        isSignupEnabled: true,
        lastUpdated: serverTimestamp(),
        updatedBy: this.currentUser.email,
        createdAt: serverTimestamp(),
        createdBy: this.currentUser.email,
        betaPhase: 'active',
        waitlistEnabled: false,
        description: 'Beta testing signup capacity management'
      };

      await setDoc(capacityRef, initialCapacityData);
      console.log('Signup capacity document created successfully');
      
      // Log this admin action
      await this.logAdminAction({
        action: 'signup_capacity_initialized',
        adminUserId: this.currentUser.uid,
        performedBy: this.currentUser.email,
        initialCapacity: 50
      });
      
      return initialCapacityData;
    } else {
      console.log('Signup capacity document already exists');
      return capacityDoc.data();
    }
  } catch (error) {
    console.error('Failed to initialize signup capacity:', error);
    throw error;
  }
}

// Get current signup capacity status
async getSignupCapacity() {
  try {
    const capacityRef = doc(db, 'signupCapacity', 'betaLimits');
    const capacityDoc = await getDoc(capacityRef);
    
    if (!capacityDoc.exists()) {
      console.log('Signup capacity document not found, creating...');
      if (this.isAdmin()) {
        return await this.initializeSignupCapacity();
      } else {
        // Return default values for non-admin users
        return {
          totalCapacity: 50,
          currentSignups: 0,
          isSignupEnabled: true,
          canSignup: true,
          spotsRemaining: 50
        };
      }
    }

    const capacityData = capacityDoc.data();
    const spotsRemaining = Math.max(0, capacityData.totalCapacity - capacityData.currentSignups);
    const canSignup = capacityData.isSignupEnabled && spotsRemaining > 0;

    return {
      ...capacityData,
      spotsRemaining,
      canSignup,
      percentageFull: Math.round((capacityData.currentSignups / capacityData.totalCapacity) * 100)
    };
  } catch (error) {
    console.error('Failed to get signup capacity:', error);
    // Return safe defaults on error
    return {
      totalCapacity: 50,
      currentSignups: 0,
      isSignupEnabled: true,
      canSignup: true,
      spotsRemaining: 50,
      error: 'Unable to verify capacity'
    };
  }
}

// Update signup capacity when new user signs up
async incrementSignupCount() {
  try {
    const capacityRef = doc(db, 'signupCapacity', 'betaLimits');
    const capacityDoc = await getDoc(capacityRef);
    
    if (!capacityDoc.exists()) {
      console.log('Creating capacity document during signup...');
      await this.initializeSignupCapacity();
    }

    // Use transaction to safely increment
    await runTransaction(db, async (transaction) => {
      const capacitySnapshot = await transaction.get(capacityRef);
      
      if (!capacitySnapshot.exists()) {
        throw new Error('Capacity document not found during transaction');
      }

      const currentData = capacitySnapshot.data();
      const newCount = currentData.currentSignups + 1;
      
      if (newCount > currentData.totalCapacity) {
        throw new Error('Signup capacity exceeded');
      }

      transaction.update(capacityRef, {
        currentSignups: newCount,
        lastUpdated: serverTimestamp(),
        lastSignupAt: serverTimestamp()
      });
    });

    console.log('Signup count incremented successfully');
  } catch (error) {
    console.error('Failed to increment signup count:', error);
    throw error;
  }
}

// Admin: Update total capacity
async updateTotalCapacity(newCapacity) {
  if (!this.isAdmin()) {
    throw new Error('Only administrators can update capacity limits');
  }

  try {
    const capacityRef = doc(db, 'signupCapacity', 'betaLimits');
    
    await updateDoc(capacityRef, {
      totalCapacity: newCapacity,
      lastUpdated: serverTimestamp(),
      updatedBy: this.currentUser.email
    });

    await this.logAdminAction({
      action: 'capacity_limit_updated',
      adminUserId: this.currentUser.uid,
      performedBy: this.currentUser.email,
      newCapacity: newCapacity
    });

    console.log('Total capacity updated to:', newCapacity);
    return { success: true, newCapacity };
  } catch (error) {
    console.error('Failed to update total capacity:', error);
    throw error;
  }
}

// Admin: Toggle signup enabled/disabled
async toggleSignupEnabled(enabled) {
  if (!this.isAdmin()) {
    throw new Error('Only administrators can toggle signup status');
  }

  try {
    const capacityRef = doc(db, 'signupCapacity', 'betaLimits');
    
    await updateDoc(capacityRef, {
      isSignupEnabled: enabled,
      lastUpdated: serverTimestamp(),
      updatedBy: this.currentUser.email
    });

    await this.logAdminAction({
      action: 'signup_status_toggled',
      adminUserId: this.currentUser.uid,
      performedBy: this.currentUser.email,
      newStatus: enabled ? 'enabled' : 'disabled'
    });

    console.log('Signup status updated to:', enabled ? 'enabled' : 'disabled');
    return { success: true, isSignupEnabled: enabled };
  } catch (error) {
    console.error('Failed to toggle signup status:', error);
    throw error;
  }
}

// Admin: Reset signup count (use carefully!)
async resetSignupCount() {
  if (!this.isAdmin()) {
    throw new Error('Only administrators can reset signup count');
  }

  try {
    // First get actual user count to verify
    const actualUserCount = await this.getBetaUserStats();
    
    const capacityRef = doc(db, 'signupCapacity', 'betaLimits');
    
    await updateDoc(capacityRef, {
      currentSignups: actualUserCount.totalUsers,
      lastUpdated: serverTimestamp(),
      updatedBy: this.currentUser.email,
      lastResetAt: serverTimestamp(),
      lastResetBy: this.currentUser.email
    });

    await this.logAdminAction({
      action: 'signup_count_reset',
      adminUserId: this.currentUser.uid,
      performedBy: this.currentUser.email,
      actualUserCount: actualUserCount.totalUsers
    });

    console.log('Signup count reset to actual user count:', actualUserCount.totalUsers);
    return { 
      success: true, 
      newCount: actualUserCount.totalUsers,
      actualUserCount: actualUserCount.totalUsers 
    };
  } catch (error) {
    console.error('Failed to reset signup count:', error);
    throw error;
  }
}

// Modified createNewUserDocument to increment signup count
async createNewUserDocument() {
  const preptuneId = this.generatePreptuneId();
  
  console.log('Creating COMPLETELY FRESH user document...');
  console.log('Email:', this.currentUser.email);
  console.log('New Preptune ID:', preptuneId);
  
  // Check capacity before creating user
  const capacityStatus = await this.getSignupCapacity();
  
  if (!capacityStatus.canSignup) {
    throw new Error('Signup capacity has been reached. Please try again later.');
  }
  
  // ✅ DO NOT extract name from email or displayName
  // ✅ Leave userName as null so AI asks for it
  
  const userData = {
    preptuneUserId: preptuneId,
    email: this.currentUser.email,
    displayName: this.currentUser.displayName || null,
    photoURL: this.currentUser.photoURL || null,
    createdAt: serverTimestamp(),
    lastActiveAt: serverTimestamp(),
    
    // ✅ Initialize clean session state
    currentChatGroupId: null,
    currentChatId: null,
    lastActiveSession: null,
    totalSessions: 0,
    
    // ✅ FIXED: userName is null until user tells chatbot their name
    settings: {
      userName: null,  // ✅ AI will ask for this
      soundEnabled: true,
      preferredVoice: null,
      preferredVoiceName: null,
      preferredVoiceConfigId: null,
      voiceSelectedAt: null,
      firstTimeCompleted: false,
      guideCompleted: false,
      selectedHosts: ['Host 1', 'Host 2'],
      newsFeeds: [],
      newsCategories: {},
      selectedStocks: [],
      selectedCryptos: [],
      showNewsCard: true,
      showStocksCard: true,
      showCryptoCard: false
    },
    
    // ✅ Podcast preferences
    podcastPreferences: {
      hosts: ['Host 1', 'Host 2'],
      guest: null,
      introMusicEnabled: true,
      talkingStyle: 'casual',
      updatedAt: serverTimestamp()
    },
    
    stats: {
      totalConversations: 0,
      totalMessages: 0,
      averageSessionDuration: 0,
      firstCallCompleted: false,
      lastCallTime: null
    },

    trialStatus: {
      startDate: serverTimestamp(),
      trialDurationDays: this.TRIAL_DURATION_DAYS,
      totalMinutesAllowed: this.TOTAL_TRIAL_MINUTES,
      isActive: true,
      type: 'trial'
    },

    trialUsage: {
      totalMinutesUsed: 0,
      totalSecondsUsed: 0,
      sessionsCount: 0,
      lastSessionDuration: 0,
      lastUsedAt: null,
      dailyUsage: {}
    },

    callTimer: {
      totalTime: this.TOTAL_TRIAL_MINUTES * 60,
      remainingTime: this.TOTAL_TRIAL_MINUTES * 60,
      lastPaused: null,
      isActive: false,
      sessionStartTime: null,
      resetCount: 0,
      lastResetBy: null,
      lastResetAt: null
    },
    
    // ✅ Initialize timezone
    timezone: this.detectUserTimezone(),
    timezoneDetectionMethod: 'auto_detected',
    timezoneUpdatedAt: serverTimestamp()
  };

  const userDocRef = doc(db, 'users', this.currentUser.uid);
  
  // ✅ Use setDoc to create fresh document
  await setDoc(userDocRef, userData, { merge: false });
  
  // Increment signup count
  try {
    await this.incrementSignupCount();
    console.log('✅ Signup count incremented for new user');
  } catch (capacityError) {
    console.error('Failed to increment signup count:', capacityError);
  }
  
  this.preptuneUserId = preptuneId;
  
  console.log(`✅ FRESH user document created (userName: null)`);
  console.log('Trial: 60 days with 20 minutes total usage');
  
  return userData;  // ✅ Return the data so ChatMode knows userName is null
}
  // Get detailed trial analytics (admin only)
  async getTrialAnalytics() {
    if (!this.isAdmin()) {
      throw new Error('Insufficient permissions - admin required');
    }

    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      
      const analytics = {
        totalUsers: 0,
        activeTrials: 0,
        expiredTrials: 0,
        averageUsageMinutes: 0,
        averageSessionsPerUser: 0,
        totalMinutesUsed: 0,
        totalSessions: 0,
        usageDistribution: {
          '0-5min': 0,
          '5-10min': 0,
          '10-15min': 0,
          '15-20min': 0,
          '20min': 0
        },
        daysUntilExpiry: {
          '0-7days': 0,
          '8-14days': 0,
          '15-30days': 0,
          '31-60days': 0,
          'expired': 0
        }
      };

      snapshot.forEach((doc) => {
        const userData = doc.data();
        const userEmail = userData.email?.toLowerCase();
        
        // Skip admin users
        if (userEmail && this.adminEmails.map(e => e.toLowerCase()).includes(userEmail)) {
          return;
        }

        analytics.totalUsers++;
        
        const trialStatus = this.calculateTrialStatus(userData);
        const minutesUsed = userData.trialUsage?.totalMinutesUsed || 0;
        const sessionsCount = userData.trialUsage?.sessionsCount || 0;
        
        analytics.totalMinutesUsed += minutesUsed;
        analytics.totalSessions += sessionsCount;
        
        if (trialStatus.isExpired) {
          analytics.expiredTrials++;
          analytics.daysUntilExpiry.expired++;
        } else {
          analytics.activeTrials++;
          
          // Categorize by days until expiry
          if (trialStatus.daysLeft <= 7) {
            analytics.daysUntilExpiry['0-7days']++;
          } else if (trialStatus.daysLeft <= 14) {
            analytics.daysUntilExpiry['8-14days']++;
          } else if (trialStatus.daysLeft <= 30) {
            analytics.daysUntilExpiry['15-30days']++;
          } else {
            analytics.daysUntilExpiry['31-60days']++;
          }
        }
        
        // Categorize by usage
        if (minutesUsed === 0) {
          analytics.usageDistribution['0-5min']++;
        } else if (minutesUsed <= 5) {
          analytics.usageDistribution['0-5min']++;
        } else if (minutesUsed <= 10) {
          analytics.usageDistribution['5-10min']++;
        } else if (minutesUsed <= 15) {
          analytics.usageDistribution['10-15min']++;
        } else if (minutesUsed <= 19.9) {
          analytics.usageDistribution['15-20min']++;
        } else {
          analytics.usageDistribution['20min']++;
        }
      });

      analytics.averageUsageMinutes = analytics.totalUsers > 0 ? 
        Number((analytics.totalMinutesUsed / analytics.totalUsers).toFixed(2)) : 0;
      analytics.averageSessionsPerUser = analytics.totalUsers > 0 ? 
        Number((analytics.totalSessions / analytics.totalUsers).toFixed(1)) : 0;

      return analytics;
    } catch (error) {
      console.error('Failed to get trial analytics:', error);
      throw error;
    }
  }

  // Cleanup with better state management
  cleanup() {
    console.log('Cleaning up FirestorePersistentStorageService...');
    
    if (this.unsubscribeAuth) {
      this.unsubscribeAuth();
      this.unsubscribeAuth = null;
    }
    
    this.currentUser = null;
    this.preptuneUserId = null;
    this.isInitialized = false;
    this.authStateReady = false;
    this.initializationPromise = null;
    
    console.log('Cleanup completed');
  }
}

export default FirestorePersistentStorageService;